/**
 * Единственный файл локализации всего сайта.
 *
 * Все интерфейсные тексты лежат здесь, сгруппированные по разделам. Русский
 * язык — источник (он в названиях ключей не участвует), остальные языки обязаны
 * иметь те же ключи. Структура вложенная: `nav.items.menu` и т. п.
 */

export type Language = 'ru' | 'en';

export const DEFAULT_LANGUAGE: Language = 'ru';

export const LANGUAGES: Array<{ code: Language; label: string }> = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
];

const ru = {
  common: {
    save: 'Сохранить',
    cancel: 'Отмена',
    delete: 'Удалить',
    edit: 'Редактировать',
    add: 'Добавить',
    create: 'Создать',
    close: 'Закрыть',
    confirm: 'Подтвердить',
    yes: 'Да',
    no: 'Нет',
    back: 'Назад',
    search: 'Поиск',
    error: 'Ошибка',
    email: 'Почта',
    loadingPage: 'Страница загружается. Пожалуйста, подождите...',
    name: 'Название',
    description: 'Описание',
    date: 'Дата',
    quantity: 'Количество',
    total: 'Итого',
    actions: 'Действия',
    all: 'Все',
    unit: 'Единица',
    unitShort: 'Ед.',
    price: 'Цена',
    user: 'Пользователь',
    retry: 'Повторить',
    unknownError: 'Неизвестная ошибка',
    notSet: 'Не задано',
    optional: 'Не обязательно',
    loadMore: 'Загрузить ещё',
    amount: 'Сумма',
    objectLabel: 'Объект:',
    objectSelectPlaceholder: 'Выберите объект',
  },
  nav: {
    groups: {
      planning: 'Планирование',
      warehouse: 'Склад',
      finance: 'Финансы',
      journals: 'Журналы',
      analytics: 'Аналитика',
      references: 'Справочники',
      extra: 'Дополнительное',
      administration: 'Администрирование',
    },
    items: {
      menu: 'Меню',
      portions: 'Порционник',
      buffet: 'Буфет',
      productsSpread: 'Раскладка продуктов',
      ttk: 'ТТК',
      receipts: 'Приход',
      balances: 'Остатки',
      writeOffs: 'Списание',
      transfers: 'Перемещение',
      accounts: 'Счета',
      journals: 'Журналы',
      statistics: 'Статистика',
      objects: 'Объекты',
      products: 'Продукты',
      diets: 'Диеты',
      management: 'Управление',
      administration: 'Администрирование',
      profile: 'Мой профиль',
    },
  },
  sidebar: {
    expandMenu: 'Развернуть меню',
    collapseMenu: 'Свернуть меню',
    logo: 'ЛОГО',
    accountMenu: 'Меню аккаунта',
    lightTheme: 'Светлая тема',
    darkTheme: 'Тёмная тема',
    logout: 'Выйти',
    language: 'Язык',
    rolePlatformAdmin: 'Администратор платформы',
    roleAdmin: 'Администратор',
    roleEmployee: 'Сотрудник',
  },
  landing: {
    menuTitle: 'Меню и ТТК',
    menuText: 'Планирование меню и технологические карты',
    warehouseTitle: 'Склад',
    warehouseText: 'Приход, остатки и списания',
    financeTitle: 'Финансы',
    financeText: 'Себестоимость и счета',
    lead: 'Управление производством питания: меню, технологические карты, склад и финансы — в одном месте.',
    signIn: 'Войти',
  },
  error: {
    title: 'Приложение упало',
    reload: 'Перезагрузить',
    log: '[onemore] необработанная ошибка рендера',
  },
  notFound: {
    goHome: 'На главную',
  },
  callback: {
    title: 'Ошибка входа',
    noDescription: 'без описания',
    unknownError: 'неизвестная ошибка',
    diagnostic:
      'Ответ вернул сервер авторизации ({{endpoint}}). Обычные причины — приложению не разрешены запрошенные скоупы, не заведён API resource {{apiResource}} или не прописан redirect URI {{origin}}/callback.',
  },
  states: {
    loadFailed: 'Не удалось загрузить данные',
    noOrgTitle: 'Нет доступных организаций',
    noOrgMessage:
      'Аккаунт не состоит ни в одной организации — разделы с организационными данными работать не будут.',
    noOrgSelected:
      'Нет организации — данные не запрашиваются. Попросите администратора добавить вас в организацию.',
  },
  noAccess: {
    sectionClosed: 'Раздел «{{title}}» вам не открыт',
    body: 'Доступ выдаёт администратор организации. Если раздел нужен для работы — попросите его добавить.',
  },
  wip: {
    imageAlt: 'Ваш проект находится на этапе «пупупу». Ожидайте',
    notDrawn: 'Раздел «{{title}}» ещё не нарисован — работы ведутся.',
  },
  writeOff: {
    editTitle: 'Редактировать списание',
    addTitle: 'Добавить списание',
    dateLabel: 'Дата:',
    datePlaceholder: 'дд/мм/гггг',
    reasonLabel: 'Причина списания:',
    reasonPlaceholder: 'Например, просрочка продукции',
    noBatches: 'На этом объекте нет партий с остатком — списывать нечего.',
    receiptLabel: 'Накладная:',
    receiptPlaceholder: 'Выберите накладную',
    productLabel: 'Товар:',
    productPlaceholder: 'Выберите товар',
    quantityLabel: 'Кол-во:',
    deleteRow: 'Удалить строку',
    addProduct: 'Добавить товар',
    total: 'Итого: {{value}}',
    fromDate: 'от {{date}}',
    productRemaining: '{{name}} — остаток {{remaining}}, {{price}}',
    errorSelectReceiptProduct: 'Выберите накладную и товар',
    errorDuplicateBatch: 'Эта партия уже есть в другой строке',
    errorEnterQuantity: 'Введите количество',
    errorNotMore: 'Не больше {{max}}',
    toastUpdated: 'Списание обновлено',
    toastCreated: 'Списание создано',
    viewTitle: 'Списание',
    viewTitleWith: 'Списание: {{reason}}',
    objectLabel: 'Объект:',
    typeLabel: 'Тип:',
    kindAuto: 'Автоматическое',
    kindManual: 'Исключительное',
    invoiceCol: 'Накладная',
    productCol: 'Товар',
    qtyCol: 'Кол-во',
    priceCol: 'Цена',
    sumCol: 'Сумма',
    grandTotal: 'Всего',
    close: 'Закрыть',
    edit: 'Редактировать',
    searchPlaceholder: 'Поиск по причине',
    kindManualPlural: 'Исключительные',
    kindAutoPlural: 'Автоматические',
    noObjects: 'Сначала заведите объект в справочнике «Объекты»',
    empty: 'Нет списаний',
    noResultsSearch: 'Ничего не нашли по запросу «{{query}}»',
    colDate: 'Дата',
    colReason: 'Причина',
    colItemsCount: 'Кол-во товаров',
  },
  transfer: {
    editTitle: 'Редактировать перемещение',
    addTitle: 'Добавить перемещение',
    fromLabel: 'Со склада:',
    toLabel: 'На склад:',
    sameObjectError: 'Склад-отправитель и склад-получатель должны различаться.',
    dateLabel: 'Дата:',
    datePlaceholder: 'дд/мм/гггг',
    noBatches: 'На этом складе нет партий с остатком — переносить нечего.',
    receiptLabel: 'Накладная:',
    receiptPlaceholder: 'Выберите накладную',
    productLabel: 'Товар:',
    productPlaceholder: 'Выберите товар',
    quantityLabel: 'Кол-во:',
    deleteRow: 'Удалить строку',
    addProduct: 'Добавить товар',
    fromDate: 'от {{date}}',
    productRemaining: '{{name}} — остаток {{remaining}}',
    errorSelectReceiptProduct: 'Выберите накладную и товар',
    errorDuplicateBatch: 'Эта партия уже есть в другой строке',
    errorEnterQuantity: 'Введите количество',
    errorNotMore: 'Не больше {{max}}',
    toastUpdated: 'Перемещение обновлено',
    toastCreated: 'Перемещение создано',
    submit: 'Переместить',
    viewTitle: 'Перемещение',
    viewTitleWith: 'Перемещение: {{from}} → {{to}}',
    fromShort: 'Откуда:',
    toShort: 'Куда:',
    invoiceCol: 'Накладная',
    productCol: 'Товар',
    qtyCol: 'Кол-во',
    close: 'Закрыть',
    edit: 'Редактировать',
    searchPlaceholder: 'Поиск по дате или объекту',
    empty: 'Нет перемещений',
    noResultsSearch: 'Ничего не нашли по запросу «{{query}}»',
    colDate: 'Дата',
    colFrom: 'Откуда',
    colTo: 'Куда',
    colItemsCount: 'Кол-во товаров',
  },
  receipt: {
    editTitle: 'Редактировать приход',
    addTitle: 'Добавить приход',
    close: 'Закрыть',
    invoiceNumberLabel: 'Номер накладной:',
    invoiceNumberPlaceholder: 'НАК-2026-001',
    dateLabel: 'Дата:',
    datePlaceholder: 'дд/мм/гггг',
    productPlaceholder: 'Название товара',
    quantityLabel: 'Кол-во:',
    unitPriceLabel: 'Цена за шт, ₽:',
    deleteProduct: 'Удалить товар',
    collapse: 'Свернуть',
    expand: 'Раскрыть',
    collapseProduct: 'Свернуть товар',
    expandProduct: 'Раскрыть товар',
    manufacturerLabel: 'Изготовитель:',
    supplierLabel: 'Поставщик:',
    packagingLabel: 'Фасовка:',
    productionDateLabel: 'Дата выработки:',
    documentTypeLabel: 'Тип документа:',
    documentTypePlaceholder: 'Выберите тип',
    documentNumberLabel: 'Номер документа:',
    expiryDateLabel: 'Срок реализации:',
    organolepticLabel: 'Органолептика:',
    organolepticPlaceholder: 'Выберите',
    storageConditionsLabel: 'Условия хранения:',
    storageConditionsPlaceholder: 'Выберите',
    noteLabel: 'Примечание:',
    notePlaceholder: 'Необязательное поле',
    addProduct: 'Добавить товар',
    total: 'Итого: {{value}}',
    submit: 'Создать приход',
    optional: 'необязательно',
    toastCreated: 'Приход создан',
    toastUpdated: 'Приход обновлён',
    viewTitle: 'Накладная {{number}}',
    viewTitlePlain: 'Накладная',
    objectLabel: 'Объект:',
    amountLabel: 'Сумма:',
    colProduct: 'Товар',
    colQuantity: 'Кол-во',
    colSupplier: 'Поставщик',
    colDocumentNumber: 'Номер документа',
    colExpiry: 'Срок реал.',
    moreInfo: 'Больше информации',
    detailManufacturer: 'Изготовитель',
    detailPackaging: 'Фасовка',
    detailProductionDate: 'Дата выработки',
    detailDocumentType: 'Тип документа',
    detailOrganoleptic: 'Органолептика',
    detailStorageConditions: 'Условия хранения',
    detailUnitPrice: 'Цена за единицу',
    detailTotal: 'Сумма',
    detailNote: 'Примечание',
    listColItemsCount: 'Кол-во товаров',
    searchPlaceholder: 'Поиск по номеру накладной',
    addButton: 'Приход',
    emptyNoObjects: 'Сначала заведите объект в справочнике «Объекты»',
    emptySearch: 'Ничего не нашли по запросу «{{query}}»',
    emptyNone: 'Нет приходов',
  },
  balances: {
    searchPlaceholder: 'Поиск по товару или накладной',
    emptyNoObjects: 'Сначала заведите объект в справочнике «Объекты»',
    emptySearch: 'Ничего не найдено по запросу',
    emptyNone: 'Нет остатков',
    colProduct: 'Товар',
    colAvgPrice: 'Средняя цена',
    colRemaining: 'Остаток',
    colInvoice: 'Накладная',
    colDate: 'Дата',
    colPrice: 'Цена',
    colReceived: 'Приход',
    colExpiry: 'Срок годности',
    expired: 'Просрочено',
    expiresSoon: 'Истекает',
    expiryLoadError: 'Не удалось загрузить сроки годности партий',
  },
  meals: {
    breakfast: 'Завтрак',
    secondBreakfast: '2-й завтрак',
    lunch: 'Обед',
    snack: 'Полдник',
    dinner: 'Ужин',
  },
  metrics: {
    kcal: 'Калорийность',
    protein: 'Белки',
    fat: 'Жиры',
    carbs: 'Углеводы',
    price: 'Цена',
  },
  units: {
    gram: 'г',
    ruble: '₽',
  },
  menu: {
    objectLabel: 'Объект:',
    objectPlaceholder: 'Выберите объект',
    dietLabel: 'Рацион:',
    dietPlaceholder: 'Выберите рацион',
    prevPeriod: 'Предыдущий период',
    nextPeriod: 'Следующий период',
    currentWeek: 'Вернуться к текущей неделе',
    fillMenu: 'Заполнение меню',
    print: 'Печать (PDF)',
    today: 'Сегодня',
    addDish: '+ блюдо',
    dayTotal: 'Итог на день',
    viewViolations: 'Посмотреть нарушения',
    defaultName: 'Меню {{range}}',
    kcalUnit: 'ккал',
    macroProtein: 'Б',
    macroFat: 'Ж',
    macroCarbs: 'У',
    filledToast: 'Меню заполнено',
    filledContent: 'Скопировано из меню «{{name}}».',
    fillError: 'Не удалось заполнить меню',
    tryAgain: 'Попробуйте ещё раз.',
    createdToast: 'Новое меню создано',
    createdContent: 'Меню «{{name}}» сохранено из текущего периода.',
    createError: 'Не удалось создать меню',
    violationAllowedToast: 'Нарушение разрешено',
    violationAllowedContent: 'День {{day}} сохранён с нарушением нормы.',
    priceExceededToast: 'Ячейка сохранена с превышением цены',
    priceExceededContent: 'Цена дня {{price}} ₽ выше лимита {{limit}} ₽.',
    slotSaved: 'Ячейка сохранена',
    createMenuTitle: 'Создание нового меню',
    fillMenuTitle: 'Заполнить меню',
    nameLabel: 'Название меню:',
    chooseLabel: 'Выбор меню:',
    choosePlaceholder: 'Выберите меню',
    startLabel: 'Начать с:',
    startPlaceholder: 'Нет будущих дней в периоде',
    createFromCurrent: 'Создать новое из текущего',
    nextDayNote: 'При выборе другого меню изменения будут применены только со следующего дня.',
    createNewMenu: 'Создать новое меню',
    choose: 'Выбрать',
    violationsTitle: 'Нарушения за день',
    violationLine: '{{label}}: {{actual}} / {{norm}} {{unit}} - {{direction}} нормы',
    below: 'ниже',
    above: 'выше',
    violationHint: 'Вы можете разрешить нарушение или изменить состав блюд в приёмах.',
    allowViolation: 'Разрешить нарушение',
    printTitle: 'Меню',
    printColDay: 'День',
    printColMeal: 'Приём',
    printColDishes: 'Блюда',
    printColKcal: 'Ккал',
    printColPrice: 'Цена',
    printResponsible: 'Ответственный за питание: ______________________',
    printFileName: 'Меню_{{range}}.pdf',
  },
  dishPicker: {
    empty: 'Блюд пока нет - добавьте из ТТК ниже.',
    kcalPrice: '{{kcal}} ккал · {{price}} ₽',
    dayPriceOver: 'Цена дня: {{price}} / {{limit}} ₽ · превышение на {{over}} ₽',
    dayPriceLeft: 'Цена дня: {{price}} / {{limit}} ₽ · осталось {{left}} ₽',
    addFromTtk: 'Добавить из ТТК',
    searchPlaceholder: 'Поиск блюда',
    loading: 'Загрузка…',
    loadError: 'Не удалось загрузить ТТК',
    exceedsLimit: ' · превысит лимит',
    add: 'Добавить',
    nothingFound: 'Ничего не найдено',
    allAdded: 'Все подходящие блюда уже в ячейке',
  },
  facility: {
    school: 'Школа',
    kindergarten: 'Детский сад',
    hospital: 'Больница',
    warehouse: 'Склад',
  },
  objects: {
    createTitle: 'Создать объект',
    editTitle: 'Редактирование объекта',
    nameLabel: 'Название объекта:',
    namePlaceholder: 'Центральный объект',
    addGroupDept: 'Добавить группу/отделение',
    headerFallback: 'Объект',
    addObject: 'Добавить объект',
    objectSelectLabel: 'Объект:',
    objectSelectAll: 'Все объекты',
    subObjectLabel: 'Подобъект:',
    colGroup: 'Группа / класс',
    colDiet: 'Рацион',
    emptySubObjectGroups: 'В подобъекте нет групп/классов',
    emptyObjectGroups: 'У объекта пока нет групп/классов',
    groupNameObject: 'Название группы/отделения:',
    groupNameClass: 'Класс (параллель):',
    groupNameGroup: 'Группа:',
    groupNameDept: 'Отделение:',
    namePlaceholderObject: 'Корпус / отделение',
    namePlaceholderClass: '1–4 классы',
    namePlaceholderGroup: 'Старшая группа',
    namePlaceholderDept: 'Терапия',
    removeGroup: 'Удалить группу',
    dietHint: 'Рацион (должен быть выбран хотя бы один)',
    noDiets: 'Диет нет — добавьте их на вкладке «Диеты».',
    addClass: 'Добавить класс',
    addGroup: 'Добавить группу',
    addDepartment: 'Добавить отделение',
    created: 'Объект создан',
    createFailed: 'Не удалось создать',
    updated: 'Объект обновлён',
    saveFailed: 'Не удалось сохранить',
    deleted: 'Объект удалён',
    deleteTitle: 'Удалить «{{name}}»?',
    deleteDescription: 'Объект используется в порционнике и меню. Выберите, как его удалить.',
    deleteAfterPeriodHint: 'До конца периода объект останется в порционнике и меню',
    deleteAfterPeriod: 'Удалить после конца периода',
    deleteNow: 'Удалить сейчас',
    deleteNowHint: 'Будет убран из всех разделов немедленно',
    subCreateTitle: 'Добавить подобъект',
    subSubtitle: 'Подобъект «{{name}}»',
    subNewSubtitle: 'Новый подобъект',
    subNameLabel: 'Название подобъекта:',
    subNamePlaceholder: 'Школа №1',
    removeSubObject: 'Удалить подобъект',
    subCreated: 'Подобъект добавлен',
    subCreateFailed: 'Не удалось добавить',
    subUpdated: 'Подобъект обновлён',
    subDeleted: 'Подобъект удалён',
    subDeleteTitle: 'Удалить «{{name}}»?',
    subDeleteDescription: 'Подобъект используется в порционнике и меню. Выберите, как его удалить.',
    subDeleteAfterPeriodHint: 'До конца периода подобъект останется в порционнике и меню',
  },
  products: {
    createTitle: 'Создать продукт',
    editTitle: 'Редактирование продукта',
    created: 'Продукт создан',
    updated: 'Продукт обновлён',
    deleted: 'Продукт удалён',
    deleteTitle: 'Удалить «{{name}}»?',
    nameLabel: 'Название продукта:',
    namePlaceholder: 'Например, Тофу соевый',
    unitLabel: 'Единица закупки:',
    unitPlaceholder: 'кг, л, шт',
    pickPlaceholder: 'Выбрать продукт',
    createOption: 'Создать «{{query}}»',
    createButton: 'Создать продукт',
    searchPlaceholder: 'Поиск по названию',
    foundCount: 'Найдено: {{count}}',
    totalCount: 'Всего продуктов: {{total}}',
    emptySearch: 'Ничего не нашли по запросу',
    empty: 'Продуктов пока нет',
    colName: 'Название',
    colUnit: 'Единица измерения',
    change: 'Изменить',
    deleteDescription: 'Продукт может использоваться в ТТК и закупках. Выберите, как его удалить.',
    deleteAfterPeriod: 'Удалить после конца периода',
    deleteAfterPeriodHint: 'До конца периода продукт останется в ТТК и приходных',
    deleteNow: 'Удалить сейчас',
    deleteNowHint: 'Будет убран из всех разделов немедленно',
  },
  staff: {
    personnel: 'Персонал',
    searchPlaceholder: 'Найти сотрудника',
    emptySearch: 'Никого не нашли по запросу «{{search}}»',
    empty: 'Сотрудников пока нет',
    pageLimit: 'Показаны первые {{shown}} из {{total}}. Уточните поиск, чтобы найти нужного.',
    colName: 'Имя',
    colEmail: 'Почта',
    colRegisteredAt: 'Регистрация',
    colRole: 'Роль',
    inviteAgain: 'Пригласить заново',
    sortAsc: 'По возрастанию — нажмите для убывания',
    sortDesc: 'По убыванию — нажмите для возрастания',
    sortBy: 'Сортировать по: {{label}}',
    statusUnspecified: 'неизвестно',
    statusInvited: 'ждёт входа',
    statusActive: 'активен',
    statusExpired: 'приглашение истекло',
    addTitle: 'Добавление нового сотрудника',
    addStep: 'Шаг {{step}}/2',
    next: 'Дальше',
    back: 'Назад',
    addAndInvite: 'Добавить и отправить приглашение',
    editTitle: 'Редактирование данных сотрудника',
    tabGeneral: 'Общая информация',
    tabDocuments: 'Документы',
    change: 'Изменить',
    deleteTitle: 'Удаление сотрудника',
    deleteBody:
      'Удалить {{name}}? Доступ к системе закроется сразу, учётная запись человека будет удалена.',
    resendTitle: 'Отправить приглашение заново',
    resendBody:
      'Выслать <bold>{{name}}</bold> новое приглашение на {{email}}? Прежняя ссылка перестанет работать.',
    send: 'Отправить',
    firstNameLabel: 'Имя:',
    firstNamePlaceholder: 'Иван',
    lastNameLabel: 'Фамилия:',
    lastNamePlaceholder: 'Иванов',
    emailLabel: 'Почта:',
    emailNote: 'На эту почту придет ссылка, с которым сотрудник войдет в систему',
    roleLabel: 'Роль:',
    rolePlaceholder: 'Выберите роль',
    orgEmailLabel: 'E-mail организации:',
    snilsLabel: 'СНИЛС:',
    innLabel: 'ИНН:',
    innPlaceholder: '12 цифр',
    passportLabel: 'Паспорт:',
    otherDocLabel: 'Другой док:',
    otherDocPlaceholder: 'Необязательно',
    created: 'Сотрудник добавлен',
    createdContent: 'Приглашение отправлено на {{email}}',
    updated: 'Данные сотрудника обновлены',
    deleted: 'Сотрудник удалён',
    invited: 'Приглашение отправлено',
    errSnilsInvalid: 'СНИЛС неверный',
    errSnilsInvalidHint: 'Не сошлось контрольное число — проверьте цифры',
    errInnInvalid: 'ИНН неверный',
    errInnInvalidHint: 'Не сошлись контрольные разряды — проверьте цифры',
    errSnilsLength: 'СНИЛС неверный',
    errSnilsLengthHint: 'В СНИЛС должно быть 11 цифр',
    errInnLength: 'ИНН неверный',
    errInnLengthHint: 'В ИНН должно быть 12 цифр',
    errPassportSeries: 'Серия паспорта неверна',
    errPassportSeriesHint: 'В серии должно быть 4 цифры',
    errPassportNumber: 'Номер паспорта неверен',
    errPassportNumberHint: 'В номере должно быть 6 цифр',
    errNonDigit: 'Недопустимые символы',
    errNonDigitHint: 'В номерах документов допустимы только цифры, пробелы, точки и дефисы',
    errEmail: 'Почта указана с ошибкой',
    errEmailHint: 'Проверьте адрес',
    errFirstName: 'Не заполнено имя',
    errLastName: 'Не заполнена фамилия',
    errAlreadyRegistered: 'Сотрудник уже зарегистрирован',
    errAlreadyRegisteredHint: 'Повторное приглашение не требуется',
    errAlreadyExists: 'Такой сотрудник уже есть',
    errAlreadyExistsHint: 'Эта почта занята другой карточкой',
    errEmailNotSent: 'Письмо не отправилось',
    errEmailNotSentHint: 'Карточка сохранена — вышлите приглашение заново из списка',
  },
  journals: {
    title: 'Автоматические журналы',
    desc: 'Каталог журналов, которые программа формирует из меню, порционника, накладных и ТТК.',
    generating: 'Журнал формируется',
    generatingContent: '{{tag}}: данные подтягиваются из связанных разделов.',
    print: 'Печать',
    auto: 'Заполняется автоматически',
    manual: 'Дозаполнить: {{fields}}',
    drawerAutoTitle: 'Заполняет программа',
    drawerManualTitle: 'Заполняет вручную',
    drawerDesc:
      'Журнал формируется из данных меню, порционника, накладных и ТТК. Поля с оценкой и подписью заполняются на месте после печати или в электронном виде.',
    close: 'Закрыть',
    source: {
      menu: 'Меню',
      ttk: 'ТТК',
      portions: 'Порционник',
      invoices: 'Накладные',
      warehouse: 'Склад',
    },
    priority: { required: 'Обязательно', optional: 'Желательно' },
    objectLabel: 'Объект',
    dietLabel: 'Рацион',
    prevPeriod: 'Предыдущий период',
    nextPeriod: 'Следующий период',
    currentPeriod: 'Текущая неделя',
    printResponsible: 'Ответственный',
    printFileName: '{{tag}} {{range}}',
    printEmptyTitle: 'Печатаем пустой бланк',
    printEmptyContent:
      'У объекта нет накладных за выбранный период — остаются пустые строки для заполнения от руки.',
    printNoDataContent:
      'За выбранную неделю нет данных для журнала — остаются пустые строки для заполнения от руки.',
    dayTotal: 'Итого за день',
    field: {
      date: 'Дата',
      meal: 'Приём пищи',
      dishes: 'Блюда',
      norm: 'Норма',
      fact: 'Факт',
      deviation: 'Отклонение',
      output: 'Выход',
      time: 'Время',
      product: 'Продукт',
      supplier: 'Поставщик',
      quantity: 'Количество',
      shelfLife: 'Срок годности',
      condition: 'Оценка состояния',
      dishType: 'Тип блюда',
      dish: 'Блюдо',
      diners: 'Число питающихся',
      vitaminDose: 'Доза витаминов',
      unit: 'Единица',
      menuQuantity: 'Количество по меню',
      toIssue: 'К выдаче',
      period: 'Период',
      opening: 'Остаток на начало',
      income: 'Приход',
      expense: 'Расход',
      closing: 'Остаток на конец',
      need: 'Потребность',
      stockBalance: 'Остаток',
      toOrder: 'К заказу',
      sampleStorage: 'Хранение проб',
      storekeeperSign: 'Подпись кладовщика',
      sign: 'Подпись',
      quality: 'Оценка',
      issuePermission: 'Разрешение на выдачу',
      arrivalDateTime: 'Дата и час поступления пищевой продукции',
      productName: 'Наименование',
      packaging: 'Фасовка',
      productionDate: 'Дата выработки',
      manufacturer: 'Изготовитель',
      arrivalQuantity: 'Количество поступившего продукта (в кг, литрах, шт)',
      safetyDocument:
        'Номер документа, подтверждающего безопасность принятого пищевого продукта (декларация о соответствии, свидетельство о государственной регистрации, документы по результатам ветеринарно-санитарной экспертизы)',
      organoleptic:
        'Результаты органолептической оценки поступившего продовольственного сырья и пищевых продуктов',
      storageConditions: 'Условия хранения, конечный срок реализации',
      actualSaleDateTime: 'Дата и час фактической реализации',
      responsibleSign: 'Подпись ответственного лица',
      note: 'Примечание',
    },
    items: {
      'journ-01': {
        tag: 'ЖУРН-01',
        title: 'Накопительная ведомость диетического контроля',
        description: 'Норма–факт за 10–14 дней по питанию.',
      },
      'journ-02': {
        tag: 'ЖУРН-02',
        title: 'Бракеражный журнал готовой продукции',
        description: 'Контроль качества готовых блюд перед выдачей.',
      },
      'journ-03': {
        tag: 'ЖУРН-03',
        title: 'Журнал контроля поступления сырья',
        description: 'Бракераж скоропортящейся продукции при приёмке.',
      },
      'journ-04': {
        tag: 'ЖУРН-04',
        title: 'Ведомость обогащения третьих и сладких блюд',
        description: 'Витаминизация блюд по типам и объёму питания.',
      },
      'journ-05': {
        tag: 'ЖУРН-05',
        title: 'Ведомость выдачи продуктов со склада',
        description: 'Меню-требование на продукты для приготовления.',
      },
      'journ-06': {
        tag: 'ЖУРН-06',
        title: 'Товарный отчёт по складу',
        description: 'Расход продуктов за выбранный период.',
      },
      'journ-07': {
        tag: 'ЖУРН-07',
        title: 'Заявка поставщику',
        description: 'Потребность в продуктах на неделю.',
      },
      'journ-08': {
        tag: 'ЖУРН-08',
        title: 'Журнал ежедневного отбора проб',
        description: 'Учёт проб готовых блюд для хранения.',
      },
    },
  },
  org: {
    rolesTitle: 'Роли и доступы',
    collapse: 'Свернуть',
    expand: 'Развернуть',
    noPermissions: 'без разрешений',
    listTitle: 'Организации',
    addButton: 'Добавить',
    empty: 'Организаций пока нет',
    disableFirstTitle: 'Сначала отключите организацию',
    disableFirstContent:
      'Удаление необратимо, поэтому доступно только для отключённых организаций.',
    deleteConfirm: 'Удалить организацию «{{name}}» безвозвратно?',
    deleted: 'Организация удалена',
    orgName: 'Название',
    memberName: 'Имя',
    colMembers: 'Участников',
    colStatus: 'Статус',
    colRoles: 'Роли',
    colCreated: 'Создана',
    colActions: 'Действия',
    colExpiresAt: 'Истекает',
    disabled: 'Отключена',
    active: 'Активна',
    disabledShort: 'отключена',
    enable: 'Включить',
    disable: 'Отключить',
    createTitle: 'Новая организация',
    createName: 'Название',
    createFirstName: 'Имя',
    createLastName: 'Фамилия',
    createAdminEmail: 'Почта администратора',
    createEmailNote: 'На этот адрес придёт ссылка для входа в систему',
    created: 'Организация создана',
    createdContent: 'Приглашение отправлено на {{email}}',
    createFailed: 'Не удалось создать организацию',
    removeMemberConfirm: 'Исключить {{email}} из организации?',
    membersTitle: 'Участники',
    membersEmpty: 'В организации пока никого нет — пригласите первого участника ниже.',
    memberSuspended: 'приостановлен',
    memberActive: 'активен',
    removeFromOrg: 'Исключить из организации',
    invitationsTitle: 'Приглашения',
    inviteEmailPlaceholder: 'почта@example.com',
    inviteRolePlaceholder: 'Роль',
    invite: 'Пригласить',
    noInvitations: 'Активных приглашений нет',
    inviteSent: 'Приглашение отправлено',
    revoke: 'Отозвать',
    statusUnspecified: 'неизвестно',
    statusPending: 'ожидает',
    statusAccepted: 'принято',
    statusExpired: 'истекло',
    statusRevoked: 'отозвано',
  },
  profile: {
    personalTitle: 'Личные данные',
    personalEmpty:
      'Не удалось загрузить данные пользователя. Имя и почта появятся, если служба входа отдаёт их в профиле.',
    fieldName: 'Имя',
    fieldLastName: 'Фамилия',
    fieldEmail: 'Электронная почта',
    securityTitle: 'Безопасность',
    securityDesc: 'Чтобы сменить пароль, подтвердите почту кодом из письма и задайте новый пароль.',
    managePassword: 'Сменить пароль',
    signOut: 'Выйти',
    preferencesTitle: 'Предпочтения',
    themeLabel: 'Тема оформления',
    themeLight: 'Светлая',
    themeDark: 'Тёмная',
    languageLabel: 'Язык интерфейса',
    languageCaption: 'Интерфейс переключается сразу, выбор сохраняется в этом браузере.',
  },
  invitation: {
    missingTitle: 'Ссылка неполная',
    missingMessage:
      'В приглашении не хватает адреса или одноразового токена. Попросите администратора выслать приглашение заново.',
    toHome: 'На главную',
    alreadyTitle: 'Вы уже вошли под другим аккаунтом',
    alreadyMessage:
      'Приглашение выписано на {{email}}. Чтобы принять его, выйдите из текущего аккаунта и откройте ссылку из письма ещё раз.',
    signOut: 'Выйти',
    stay: 'Остаться в текущем аккаунте',
    failedTitle: 'Не удалось начать вход',
    retry: 'Попробовать снова',
    accepting: 'Принимаем приглашение…',
    redirecting: 'Сейчас откроется страница входа для {{email}}.',
    continueManually: 'Продолжить вручную',
  },
  diets: {
    nameLabel: 'Название:',
    namePlaceholder: 'Например, Основной',
    institutionsLabel: 'Для каких учреждений доступна диета:',
    mealsLabel: 'Для каких приёмов пищи доступна диета:',
    dailyNorm: 'Норма на день',
    pricePerDayLabel: 'Цена на день, ₽:',
    formKcal: 'Ккал:',
    formProtein: 'Белки, г:',
    formFat: 'Жиры, г:',
    formCarbs: 'Углеводы, г:',
    normError: 'Не может быть отрицательным',
    availableFor: 'Доступна для:',
    mealsTitle: 'Приёмы пищи:',
    viewPrice: 'Цена:',
    viewKcal: 'Калорийность:',
    viewProtein: 'Белки:',
    viewFat: 'Жиры:',
    viewCarbs: 'Углеводы:',
    editDietTitle: 'Редактировать диету',
    createDietTitle: 'Создать диету',
    createDiet: 'Создать диету',
    deleteConfirmTitle: 'Удалить «{{name}}»?',
    deleteConfirmText: 'Диета используется в объектах и меню. Выберите, как её удалить.',
    deleteAfterPeriod: 'Удалить после конца периода',
    deleteAfterPeriodHint: 'До конца периода диета останется в объектах и меню',
    deleteNow: 'Удалить сейчас',
    deleteNowHint: 'Будет убрана из всех разделов немедленно',
    updatedToast: 'Диета сохранена',
    deletedToast: 'Диета удалена',
    createdToast: 'Диета создана',
    searchPlaceholder: 'Поиск по названию диеты',
    total: 'Всего диет: {{count}}',
    emptySearch: 'Ничего не нашли по запросу «{{query}}»',
    emptyNone: 'Диет пока нет',
    summary: '{{price}} ₽ · {{kcal}} ккал · Б: {{protein}} г · Ж: {{fat}} г · У: {{carbs}} г',
  },
  spread: {
    objectLabel: 'Объект:',
    objectPlaceholder: 'Выберите объект',
    wholeOrg: 'Вся организация',
    prevDay: 'Предыдущий день',
    nextDay: 'Следующий день',
    byMeals: 'По приёмам пищи',
    byProducts: 'По продуктам',
    printPdf: 'Скачать раскладку PDF',
    searchPlaceholder: 'Поиск по продуктам...',
    foundProducts: 'Найдено: {{count}} продуктов',
    totalProducts: 'Всего продуктов: {{count}}',
    confirmedToast: 'Товары списаны со склада',
    alreadyConfirmed: 'Товары уже списаны со склада',
    confirmHint: 'Подтвердить списание можно за прошедшие дни, сегодня и завтра',
    confirmProducts: 'Подтвердить списание товаров',
    emptySpread: 'Раскладки нет: заполните порционник на эту дату',
    confirmTitle: 'Подтверждение списания',
    confirmText: 'Проверьте информацию, изменить это будет нельзя.',
    ok: 'ОК',
    colProduct: 'Продукт',
    colTotalDay: 'Всего на день',
    colDish: 'Блюдо',
    colMeal: 'Приём пищи',
    colPortions: 'Порций',
    colQuantity: 'Кол-во',
    printTitle: 'Раскладка продуктов',
    printPositions: 'Итого позиций: {{count}}',
    printTotalDay: 'Всего продуктов на день: {{count}}',
    printFileName: 'Раскладка_{{date}}.pdf',
  },
  accounts: {
    objectLabel: 'Объект:',
    allObjects: 'Все объекты',
    totalAccounts: 'Всего счетов: {{count}}',
    found: 'Найдено: {{count}}',
    passedPeople: 'Прошли: {{count}} чел.',
    chargedRub: 'Списано: {{amount}} ₽',
    people: '{{count}} чел.',
    amountRub: '{{amount}} ₽',
    print: 'Печать',
    colOrganization: 'Организация',
    colPassed: 'Прошли',
    colCharged: 'Списано',
  },
  portions: {
    prevDay: 'Предыдущий день',
    nextDay: 'Следующий день',
    byObjects: 'По объектам',
    byDishes: 'По блюдам',
    titleObjects: 'Порционник по объектам',
    titleDishes: 'Порционник по блюдам',
    searchObjects: 'Поиск по объектам...',
    searchDishes: 'Поиск по блюдам...',
    objectLabel: 'Объект:',
    objectPlaceholder: 'Выберите объект или подобъект',
    print: 'Печать (PDF)',
    futureDate: 'Эта дата в будущем',
    goToday: 'Перейти на сегодня',
    fillYesterday: 'Заполнить вчерашними данными',
    emptyAddSubObjects: 'Сначала добавьте подобъекты в разделе «Объекты»',
    fillError: 'Не удалось заполнить',
    saveError: 'Не удалось сохранить',
    stickersTitle: 'Наклейки на термос',
    stickerTitle: 'Наклейка: {{dish}}',
    expiryLabel: 'Срок годности:',
    expiryPlaceholder: 'Например «72 часа» или «до 12.10.2026»',
    downloadPdf: 'Скачать PDF',
    total: 'Всего',
    emptySummary: 'Нет данных для сводного порционника',
    colObject: 'Объект',
    colDepartment: 'Отделение',
    colTable: 'Стол',
    colPeople: 'Человек',
    colDish: 'Блюдо',
    colDiet: 'Рацион',
    colPortions: 'Порций',
    subObjectOf: 'подобъект {{name}}',
    noData: 'Нет данных',
    fillByOrg: 'Заполните порционник по организациям',
    fillPortions: 'Заполнить порционник',
    grandTotalLabel: 'Общее количество всех блюд',
    stickerTooltip: 'Наклейка на термос',
    kg: 'кг',
    printTitle: 'Порционник',
    printResponsible: 'Ответственный за питание: ______________________',
    printFileName: 'Порционник_{{date}}.pdf',
    stickerWeight: 'Масса одной порции: {{grams}} г',
    stickerPortionsKg: '{{portions}} порций ({{kg}} кг)',
    stickerComposition: 'Состав: {{composition}}',
    stickerKbju: 'КБЖУ: Б {{protein}} · Ж {{fat}} · У {{carbs}} · {{kcal}} ккал',
    stickerDate: 'Дата: {{date}}',
    stickerExpiry: 'Срок: {{expiry}}',
    stickerFileName: 'Наклейки_{{date}}.pdf',
  },
  buffet: {
    prevDay: 'Предыдущий день',
    nextDay: 'Следующий день',
    assortment: 'Ассортимент',
    transfer: 'Передать',
    sale: 'Продажи',
    writeOff: 'Списать',
    revenue: 'Выручка',
    cost: 'Себестоимость',
    margin: 'Маржа',
    writeOffs: 'Списания',
    colProduct: 'Товар',
    colPrice: 'Цена',
    colOpening: 'На начало',
    colReceived: 'Приход',
    colSold: 'Продано',
    colWrittenOff: 'Списано',
    colClosing: 'Остаток',
    colRevenue: 'Выручка',
    emptyNoObjects: 'Сначала добавьте объект в разделе «Объекты»',
    emptyAssortment: 'В ассортименте пока нет товаров',
    emptyNoStock: 'Нет товаров с остатком — сначала передайте их со склада',
    emptyDay: 'За {{date}} движений нет',
    setupAssortment: 'Настроить ассортимент',
    assortmentTitle: 'Ассортимент',
    assortmentHint:
      'Выберите продукты из общего справочника и укажите цену продажи. Закупочные цены остаются на складе.',
    searchProducts: 'Поиск по продуктам...',
    noProducts: 'В справочнике нет продуктов. Добавьте их в разделе «Продукты».',
    emptyProductSearch: 'По запросу «{{query}}» ничего не найдено',
    salePricePlaceholder: 'Цена, ₽',
    priceRequired: 'Укажите цену больше нуля для выбранных товаров',
    assortmentSaved: 'Ассортимент сохранён',
    transferTitle: 'Передача со склада',
    saleTitle: 'Внесение продаж',
    writeOffTitle: 'Списание',
    reason: 'Причина списания',
    reasonPlaceholder: 'Например «истёк срок годности»',
    productPlaceholder: 'Выберите товар',
    productRemaining: '{{name}} · остаток {{remaining}}',
    errorSelectProduct: 'Выберите товар',
    errorDuplicateProduct: 'Товар уже добавлен в другой строке',
    errorQuantity: 'Укажите количество больше нуля',
    errorNotMore: 'Не больше {{max}}',
    addRow: 'Добавить строку',
    applyMovement: 'Провести',
    transferSaved: 'Передача проведена',
    saleSaved: 'Продажи внесены',
    writeOffSaved: 'Списание проведено',
    dataUnavailable: 'Не удалось загрузить данные. Обновите страницу.',
  },
  ttk: {
    title: 'ТТК',
    createTitle: 'Создать ТТК',
    editTitle: 'Редактировать ТТК',
    editDishTitle: 'Редактировать блюдо',
    created: 'ТТК создана',
    saved: 'ТТК сохранена',
    deleted: 'ТТК удалена',
    deletedInMenu: 'Останется в меню до конца периода',
    saveCurrent: 'Сохранить в текущей',
    saveNew: 'Сохранить в новой',
    yieldLabel: 'Выход:',
    collectionLabel: 'Сборник:',
    technologyLabel: 'Технология:',
    noIngredients: 'Ингредиенты не указаны',
    colTitle: 'Название',
    colCollection: 'Сборник',
    colPrice: 'Цена',
    colKcal: 'Ккал',
    colName: 'Наименование',
    colGross1: '1 порция, брутто',
    colNet1: '1 порция, нетто',
    colGross100: '100 порций, брутто',
    colNet100: '100 порций, нетто',
    colUnitPrice: 'Цена за ед.',
    colSum: 'Сумма',
    noStock: 'нет остатков',
    proteinG: 'Белки, г',
    fatG: 'Жиры, г',
    carbsG: 'Углеводы, г',
    kcalCol: 'Энерг. ценность, ккал',
    nameLabel: 'Название:',
    namePlaceholder: 'Название блюда',
    collectionNameLabel: 'Название сборника:',
    collectionNamePlaceholder: 'Название сборника',
    optional: 'опционально',
    technologySection: 'Технология приготовления',
    technologyPlaceholder: 'Опишите технологию приготовления',
    nutritionSection: 'Пищевая ценность на порцию',
    proteinLabel: 'Белки, г:',
    fatLabel: 'Жиры, г:',
    carbsLabel: 'Углеводы, г:',
    kcalLabel: 'Калории, ккал:',
    ingredientsSection: 'Ингредиенты',
    productNamePlaceholder: 'Название продукта',
    grossLabel: 'Брутто на 1 порц:',
    netLabel: 'Нетто на 1 порц:',
    removeIngredient: 'Удалить ингредиент',
    addIngredient: 'Добавить ингредиент',
    errorSelectProduct: 'Выберите продукт из справочника',
    errorGrossPositive: 'Брутто должно быть больше нуля',
    errorGrossDecimals: 'В брутто не больше трёх знаков после запятой',
    errorNetRange: 'Нетто от нуля до брутто',
    errorNetDecimals: 'В нетто не больше трёх знаков после запятой',
    errorDuplicateProduct: 'Этот продукт уже есть в карте',
    requiredField: 'Обязательное поле',
    allCollections: 'Все',
    noCollection: 'Без сборника',
    searchPlaceholder: 'Поиск по названию блюда или карты',
    foundCount: 'Найдено: {{count}} ТТК',
    totalCount: 'Всего ТТК: {{count}}',
    nothingFound: 'Ничего не нашли по заданным фильтрам',
    empty: 'Карт ТТК пока нет',
    sortAsc: 'По возрастанию — нажмите для убывания',
    sortDesc: 'По убыванию — нажмите для возрастания',
    sortBy: 'Сортировать по: {{label}}',
    importHeader: 'Импорт сборников',
    importIntro: 'Импортируйте ваши сборники',
    importButton: 'Импортировать',
    importCollection: 'Импорт сборника',
    importUnsupported: 'Неподдерживаемый формат',
    importOnlyFormats: 'Можно загружать только {{formats}}',
    importSomeFailed: 'Не все файлы разобраны',
    importEmptyFiles: 'Не нашли блюд в: {{files}}',
    importNone: 'Блюд не найдено',
    importNoneHint: 'Проверьте, что в файле есть технологические карты или названия блюд.',
    importParseError: 'Ошибка разбора файла',
    importNotConnected: 'Импорт сборников пока не подключён',
    importNotConnectedHint: 'Карты из файла не сохранены. Заведите их вручную через «Создать ТТК».',
    renameFile: 'Изменить имя файла',
    removeFile: 'Убрать файл',
    collapse: 'Свернуть',
    showDishes: 'Показать блюда',
    renameCollection: 'Изменить название',
    removeCollection: 'Убрать сборник',
    openEdit: 'Открыть и изменить',
    removeDish: 'Убрать блюдо',
    chooseFile: 'Выберите файл',
    or: 'или',
    dropHere: 'перетащите файл сюда',
    deleteHeader: 'Удалить «{{name}}»?',
    deleteDescription: 'ТТК используется в меню и других разделах. Выберите, как её удалить.',
    deleteSoft: 'Удалить после конца периода',
    deleteSoftHint: 'До конца периода ТТК останется в меню и других разделах',
    deleteNow: 'Удалить сейчас',
    deleteNowHint: 'Будет убрана из всех разделов немедленно',
  },
  apiErrors: {
    fallback: {
      network: 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз',
      unknown: 'Что-то пошло не так. Попробуйте ещё раз',
      invalidArgument: 'Проверьте введённые данные',
      notFound: 'Не найдено — возможно, запись уже удалили. Обновите страницу',
      alreadyExists: 'Такая запись уже есть',
      permissionDenied: 'Недостаточно прав для этого действия',
      conflict: 'Сейчас это сделать нельзя: данные изменились или используются в других разделах',
      rateLimited: 'Слишком много запросов. Подождите немного и попробуйте снова',
      unavailable: 'Сервис временно недоступен. Попробуйте позже',
      unauthenticated: 'Сессия истекла. Войдите заново',
    },
    codes: {
      BAD_REQUEST: 'Не удалось выполнить запрос. Обновите страницу и попробуйте ещё раз',
      BATCH_DUPLICATE: 'Строка {{line}}: эта партия уже есть в документе',
      BATCH_NOT_FOUND: 'Строка {{line}}: партия не найдена — возможно, накладную изменили',
      BATCH_OTHER_OBJECT: 'Строка {{line}}: партия числится на другом объекте',
      BATCH_RECEIVED_AFTER_DATE:
        'Строка {{line}}: партия пришла {{receiptDate}} — дата документа не может быть раньше',
      BATCH_REMAINING_EXCEEDED:
        'Строка {{line}}: в партии осталось только {{remainingQuantity}}, а указано {{quantity}}',
      BATCH_REQUIRED: 'Строка {{line}}: выберите партию',
      DATE_INVALID: 'Дата указана неверно',
      DATE_RANGE_INVALID: 'Дата окончания раньше даты начала',
      DATE_REQUIRED: 'Укажите дату',
      DIET_NAME_EMPTY: 'Укажите название диеты',
      DIET_NORM_NEGATIVE: 'Нормы диеты не могут быть отрицательными',
      DIET_NOT_FOUND: 'Диета не найдена — возможно, её удалили',
      EMPLOYEE_ALREADY_EXISTS: 'Такой сотрудник уже есть',
      EMPLOYEE_ALREADY_REGISTERED: 'Сотрудник уже зарегистрировался — приглашение больше не нужно',
      EMPLOYEE_EMAIL_INVALID: 'Проверьте адрес почты сотрудника',
      EMPLOYEE_EMAIL_TAKEN: 'Эта почта уже используется — укажите другую',
      EMPLOYEE_FIELD_DIGITS_COUNT: 'В поле «{{field}}» должно быть цифр: {{digits}}',
      EMPLOYEE_FIELD_DIGITS_ONLY: 'В поле «{{field}}» можно вводить только цифры',
      EMPLOYEE_FIELD_REQUIRED: 'Заполните поле «{{field}}»',
      EMPLOYEE_INN_INVALID: 'ИНН указан с ошибкой — проверьте цифры',
      EMPLOYEE_NOT_FOUND: 'Сотрудник не найден — возможно, его уже удалили',
      EMPLOYEE_SNILS_INVALID: 'СНИЛС указан с ошибкой — проверьте цифры',
      INVITATION_EMAIL_INVALID: 'Проверьте адрес почты для приглашения',
      INVITATION_EMAIL_NOT_SENT:
        'Карточка сохранена, но письмо с приглашением не отправилось — вышлите приглашение заново',
      INVITATION_EMAIL_REQUIRED: 'Укажите почту, на которую отправить приглашение',
      INVITATION_NOT_FOUND: 'Приглашение не найдено — возможно, его уже отозвали',
      ITEMS_REQUIRED: 'Добавьте хотя бы одну строку',
      MEMBER_NOT_FOUND: 'Участник не найден — возможно, его уже исключили',
      MENU_DUPLICATE_RECIPE: 'Это блюдо уже есть в приёме пищи',
      MENU_NAME_EMPTY: 'Укажите название меню',
      MENU_NOT_FOUND: 'Меню не найдено — возможно, его удалили',
      MENU_RANGE_TOO_LONG: 'Период не может быть длиннее {{maxDays}} дней',
      MENU_RECIPE_NOT_FOUND: 'Одна из техкарт не найдена — возможно, её удалили',
      NOT_ENOUGH_STOCK: 'Не хватает продуктов на складе: {{items}}',
      NUMBER_INVALID: 'Введите число',
      OBJECT_DIET_NOT_FOUND: 'Одна из диет в группах не найдена — возможно, её удалили',
      OBJECT_GROUP_NAME_EMPTY: 'Укажите название группы в строке {{line}}',
      OBJECT_HAS_RECEIPTS: 'По объекту есть приходные накладные — удалить его нельзя',
      OBJECT_NAME_EMPTY: 'Укажите название объекта',
      OBJECT_NOT_FOUND: 'Объект не найден — возможно, его удалили',
      OBJECT_TYPE_UNKNOWN: 'Выберите тип объекта из списка',
      ORGANIZATION_ADMIN_EMAIL_TAKEN:
        'Эта почта уже используется — укажите для администратора другую',
      ORGANIZATION_IS_PLATFORM: 'Служебную организацию платформы удалить нельзя',
      ORGANIZATION_NAME_EMPTY: 'Укажите название организации',
      ORGANIZATION_NOT_DISABLED: 'Сначала отключите организацию, потом её можно удалить',
      ORGANIZATION_NOT_FOUND: 'Организация не найдена — возможно, её уже удалили',
      ORGANIZATION_REQUIRED: 'Выберите организацию',
      PORTIONS_COPY_SAME_DAY: 'Нельзя скопировать день сам в себя — выберите другую дату',
      PORTIONS_GROUP_DIET_NOT_FOUND:
        'Группа с такой диетой в объекте не найдена — возможно, объект изменили',
      PORTIONS_HEADCOUNT_NEGATIVE: 'Количество человек не может быть отрицательным',
      PRODUCT_HAS_OPEN_BATCHES:
        'Продукт есть на складе — удалить его можно, когда остаток закончится',
      PRODUCT_HAS_RECEIPTS: 'Продукт уже поступал на склад — изменить его нельзя',
      PRODUCT_NAME_EMPTY: 'Укажите название продукта',
      PRODUCT_NOT_FOUND: 'Продукт не найден — возможно, его удалили',
      PRODUCT_REQUIRED: 'Строка {{line}}: выберите продукт',
      PRODUCT_UNIT_UNKNOWN: 'Выберите единицу измерения из списка',
      PRODUCT_USED_IN_RECIPES: 'Продукт используется в техкартах — изменить или удалить его нельзя',
      QUANTITY_NOT_POSITIVE: 'Строка {{line}}: количество должно быть больше нуля',
      RECEIPT_BELONGS_TO_TRANSFER: 'Эта накладная создана перемещением — измените само перемещение',
      RECEIPT_DOCUMENT_NUMBER_REQUIRED: 'Строка {{line}}: укажите номер документа',
      RECEIPT_DOCUMENT_TYPE_REQUIRED: 'Строка {{line}}: укажите вид документа',
      RECEIPT_EXPIRY_DATE_REQUIRED: 'Строка {{line}}: укажите срок годности',
      RECEIPT_HAS_WRITE_OFFS:
        'Из этой накладной уже списывали или перемещали продукты — изменить её нельзя',
      RECEIPT_INVOICE_NUMBER_REQUIRED: 'Укажите номер накладной',
      RECEIPT_ITEM_NOT_FOUND:
        'Строка накладной не найдена — обновите страницу и попробуйте ещё раз',
      RECEIPT_MANUFACTURER_REQUIRED: 'Строка {{line}}: укажите производителя',
      RECEIPT_NOT_FOUND: 'Накладная не найдена',
      RECEIPT_ORGANOLEPTIC_REQUIRED: 'Строка {{line}}: укажите результат органолептической оценки',
      RECEIPT_PACKAGING_REQUIRED: 'Строка {{line}}: укажите фасовку',
      RECEIPT_PRODUCTION_DATE_REQUIRED: 'Строка {{line}}: укажите дату изготовления',
      RECEIPT_STORAGE_CONDITIONS_REQUIRED: 'Строка {{line}}: укажите условия хранения',
      RECEIPT_SUPPLIER_REQUIRED: 'Строка {{line}}: укажите поставщика',
      RECIPE_DUPLICATE_PRODUCT: 'Продукт в строке {{line}} уже есть в составе',
      RECIPE_GROSS_NOT_POSITIVE: 'Брутто в строке {{line}} должно быть больше нуля',
      RECIPE_INGREDIENT_PRODUCT_EMPTY: 'Выберите продукт в строке {{line}}',
      RECIPE_NAME_EMPTY: 'Укажите название техкарты',
      RECIPE_NET_OUT_OF_RANGE: 'Нетто в строке {{line}} должно быть от нуля до брутто',
      RECIPE_NOT_FOUND: 'Техкарта не найдена — возможно, её удалили',
      RECIPE_NUTRITION_NEGATIVE: 'Пищевая ценность не может быть отрицательной',
      RECIPE_PRODUCT_NOT_FOUND: 'Один из продуктов техкарты не найден — возможно, его удалили',
      RECIPE_USED_IN_MENU: 'Техкарта используется в меню — изменить или удалить её нельзя',
      RECIPE_YIELD_NEGATIVE: 'Выход порции не может быть отрицательным',
      ROLE_NOT_ASSIGNABLE: 'Эту роль нельзя назначить сотруднику — выберите другую',
      SPREAD_ALREADY_CONFIRMED: 'Раскладка на {{date}} уже подтверждена',
      SPREAD_DATE_TOO_LATE: 'Раскладку можно подтвердить не дальше чем на завтра',
      SPREAD_EMPTY: 'На {{date}} нет раскладки продуктов',
      TOO_MANY_DECIMALS: 'Не больше {{max}} знаков после запятой',
      TRANSFER_FROM_OBJECT_REQUIRED: 'Выберите объект, с которого перемещаете продукты',
      TRANSFER_HAS_WRITE_OFFS:
        'Перемещённые продукты уже списывали или перемещали дальше — изменить перемещение нельзя',
      TRANSFER_NOT_FOUND: 'Перемещение не найдено',
      TRANSFER_SAME_OBJECTS: 'Нельзя переместить продукты на тот же объект',
      TRANSFER_TO_OBJECT_REQUIRED: 'Выберите объект, на который перемещаете продукты',
      UNIT_PRICE_NEGATIVE: 'Строка {{line}}: цена не может быть отрицательной',
      WRITE_OFF_AUTO_NOT_EDITABLE: 'Автосписание по раскладке изменить нельзя',
      WRITE_OFF_NOT_FOUND: 'Списание не найдено',
      WRITE_OFF_REASON_REQUIRED: 'Укажите причину списания',
    },
    fields: {
      email: 'Почта',
      firstName: 'Имя',
      inn: 'ИНН',
      lastName: 'Фамилия',
      passportNumber: 'Номер паспорта',
      passportSeries: 'Серия паспорта',
      roleId: 'Роль',
      snils: 'СНИЛС',
    },
  },
  statistics: {
    objectLabel: 'Объект:',
    allObjects: 'Все объекты',
    preset7: '7 дней',
    preset30: '30 дней',
    presetMonth: 'Этот месяц',
    presetPrevMonth: 'Прошлый месяц',
    demoEnable: 'Демо-данные',
    demoDisable: 'Реальные данные',
    demoBadge: 'Демо-данные',
    demoHint:
      'Сгенерированные счета за последние четыре месяца — чтобы посмотреть страницу без реальных данных.',
    kpiPeople: 'Человеко-дней',
    kpiPeopleHint: 'В среднем {{value}} чел. в день',
    kpiCharged: 'Списано за период',
    kpiChargedHint: 'В среднем {{value}} в день',
    kpiPerPerson: 'Стоимость человека-дня',
    kpiPerPersonHint: 'Сколько стоит один человек за день',
    kpiDays: 'Дней с данными',
    kpiDaysHint: 'Заполнено {{percent}} дней периода',
    chartPeople: 'Люди по дням',
    chartCharged: 'Расход по дням',
    chartEmpty: 'За выбранный период данных нет',
    maxValue: 'Максимум: {{value}}',
    byDays: 'По дням',
    byMonths: 'По месяцам',
    avgPerMonth: 'В среднем в месяц: {{value}}',
    byObjects: 'По объектам',
    byDiets: 'По диетам',
    byMeals: 'По приёмам пищи',
    colDate: 'Дата',
    colMonth: 'Месяц',
    colObject: 'Объект',
    colPeople: 'Люди',
    colPeopleDays: 'Человеко-дни',
    colCharged: 'Списано',
    colPerPerson: '₽/чел',
    colPerDay: '₽/день',
    colShare: 'Доля',
    portionsValue: '{{value}} порц.',
    daysWithoutData: 'Дней без счетов: {{count}}',
    total: 'Итого',
    empty:
      'За выбранный период счетов нет. Они появляются после подтверждения раскладки продуктов.',
    budgetTitle: 'Расход и лимит',
    budgetHint:
      'Сколько реально списано на питание и сколько позволял лимит: лимит — цена дня рациона, умноженная на людей на этом рационе за день.',
    budgetSpent: 'Потрачено',
    budgetPeopleDays: '{{value}} человеко-дней',
    budgetLimit: 'Лимит',
    budgetLimitHint: 'По ценам дня рационов',
    budgetPercent: 'Использовано лимита',
    budgetSaved: 'Экономия {{value}}',
    budgetOver: 'Перерасход {{value}}',
    budgetWithoutLimit:
      'Ещё {{value}} списано по рационам без цены дня (удалены или цена не задана) — в сравнение с лимитом не вошло.',
    budgetByDiets: 'Лимит по рационам',
    budgetByObjects: 'Лимит по объектам',
    colDiet: 'Рацион',
    colSpent: 'Потрачено',
    colLimit: 'Лимит',
    colLimitPercent: '%',
  },
};

const en = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    back: 'Back',
    search: 'Search',
    error: 'Error',
    email: 'Email',
    loadingPage: 'The page is loading. Please wait...',
    name: 'Name',
    description: 'Description',
    date: 'Date',
    quantity: 'Quantity',
    total: 'Total',
    actions: 'Actions',
    all: 'All',
    unit: 'Unit',
    unitShort: 'Unit',
    price: 'Price',
    user: 'User',
    retry: 'Retry',
    unknownError: 'Unknown error',
    notSet: 'Not set',
    optional: 'Optional',
    loadMore: 'Load more',
    amount: 'Amount',
    objectLabel: 'Facility:',
    objectSelectPlaceholder: 'Select a facility',
  },
  nav: {
    groups: {
      planning: 'Planning',
      warehouse: 'Warehouse',
      finance: 'Finance',
      journals: 'Journals',
      analytics: 'Analytics',
      references: 'Reference',
      extra: 'Additional',
      administration: 'Administration',
    },
    items: {
      menu: 'Menu',
      portions: 'Portioning',
      buffet: 'Point of sale',
      productsSpread: 'Product layout',
      ttk: 'Tech cards',
      receipts: 'Receipts',
      balances: 'Balances',
      writeOffs: 'Write-offs',
      transfers: 'Transfers',
      accounts: 'Invoices',
      journals: 'Journals',
      statistics: 'Statistics',
      objects: 'Objects',
      products: 'Products',
      diets: 'Diets',
      management: 'Management',
      administration: 'Administration',
      profile: 'My profile',
    },
  },
  sidebar: {
    expandMenu: 'Expand menu',
    collapseMenu: 'Collapse menu',
    logo: 'LOGO',
    accountMenu: 'Account menu',
    lightTheme: 'Light theme',
    darkTheme: 'Dark theme',
    logout: 'Sign out',
    language: 'Language',
    rolePlatformAdmin: 'Platform administrator',
    roleAdmin: 'Administrator',
    roleEmployee: 'Employee',
  },
  landing: {
    menuTitle: 'Menu & tech cards',
    menuText: 'Menu planning and technological cards',
    warehouseTitle: 'Warehouse',
    warehouseText: 'Receipts, balances and write-offs',
    financeTitle: 'Finance',
    financeText: 'Cost price and invoices',
    lead: 'Food production management: menu, tech cards, warehouse and finance — all in one place.',
    signIn: 'Sign in',
  },
  error: {
    title: 'The app crashed',
    reload: 'Reload',
    log: '[onemore] unhandled render error',
  },
  notFound: {
    goHome: 'Go home',
  },
  callback: {
    title: 'Sign-in error',
    noDescription: 'no description',
    unknownError: 'unknown error',
    diagnostic:
      'The response came from the authorization server ({{endpoint}}). Common causes: the app is not allowed the requested scopes, the API resource {{apiResource}} is not registered, or the redirect URI {{origin}}/callback is not configured.',
  },
  states: {
    loadFailed: 'Failed to load data',
    noOrgTitle: 'No organizations available',
    noOrgMessage:
      'Your account does not belong to any organization — sections with organization data will not work.',
    noOrgSelected:
      'No organization selected — data is not requested. Ask an administrator to add you to an organization.',
  },
  noAccess: {
    sectionClosed: 'The “{{title}}” section is not available to you',
    body: 'Access is granted by the organization administrator. If you need this section for work — ask them to add it.',
  },
  wip: {
    imageAlt: 'Your project is at the “wip” stage. Stay tuned',
    notDrawn: 'The “{{title}}” section is not finished yet — work is in progress.',
  },
  writeOff: {
    editTitle: 'Edit write-off',
    addTitle: 'Add write-off',
    dateLabel: 'Date:',
    datePlaceholder: 'dd/mm/yyyy',
    reasonLabel: 'Write-off reason:',
    reasonPlaceholder: 'For example, expired products',
    noBatches: 'This facility has no batches with stock left — nothing to write off.',
    receiptLabel: 'Receipt:',
    receiptPlaceholder: 'Select a receipt',
    productLabel: 'Product:',
    productPlaceholder: 'Select a product',
    quantityLabel: 'Qty:',
    deleteRow: 'Delete row',
    addProduct: 'Add product',
    total: 'Total: {{value}}',
    fromDate: 'dated {{date}}',
    productRemaining: '{{name}} — remaining {{remaining}}, {{price}}',
    errorSelectReceiptProduct: 'Select a receipt and product',
    errorDuplicateBatch: 'This batch is already in another row',
    errorEnterQuantity: 'Enter a quantity',
    errorNotMore: 'No more than {{max}}',
    toastUpdated: 'Write-off updated',
    toastCreated: 'Write-off created',
    viewTitle: 'Write-off',
    viewTitleWith: 'Write-off: {{reason}}',
    objectLabel: 'Facility:',
    typeLabel: 'Type:',
    kindAuto: 'Automatic',
    kindManual: 'Exceptional',
    invoiceCol: 'Invoice',
    productCol: 'Product',
    qtyCol: 'Qty',
    priceCol: 'Price',
    sumCol: 'Amount',
    grandTotal: 'Total',
    close: 'Close',
    edit: 'Edit',
    searchPlaceholder: 'Search by reason',
    kindManualPlural: 'Exceptional',
    kindAutoPlural: 'Automatic',
    noObjects: 'First create a facility in the “Facilities” catalog',
    empty: 'No write-offs',
    noResultsSearch: 'Nothing found for the query “{{query}}”',
    colDate: 'Date',
    colReason: 'Reason',
    colItemsCount: 'Products',
  },
  transfer: {
    editTitle: 'Edit transfer',
    addTitle: 'Add transfer',
    fromLabel: 'From warehouse:',
    toLabel: 'To warehouse:',
    sameObjectError: 'The source and destination warehouses must be different.',
    dateLabel: 'Date:',
    datePlaceholder: 'dd/mm/yyyy',
    noBatches: 'This warehouse has no batches with stock left — nothing to transfer.',
    receiptLabel: 'Receipt:',
    receiptPlaceholder: 'Select a receipt',
    productLabel: 'Product:',
    productPlaceholder: 'Select a product',
    quantityLabel: 'Qty:',
    deleteRow: 'Delete row',
    addProduct: 'Add product',
    fromDate: 'dated {{date}}',
    productRemaining: '{{name}} — remaining {{remaining}}',
    errorSelectReceiptProduct: 'Select a receipt and product',
    errorDuplicateBatch: 'This batch is already in another row',
    errorEnterQuantity: 'Enter a quantity',
    errorNotMore: 'No more than {{max}}',
    toastUpdated: 'Transfer updated',
    toastCreated: 'Transfer created',
    submit: 'Transfer',
    viewTitle: 'Transfer',
    viewTitleWith: 'Transfer: {{from}} → {{to}}',
    fromShort: 'From:',
    toShort: 'To:',
    invoiceCol: 'Invoice',
    productCol: 'Product',
    qtyCol: 'Qty',
    close: 'Close',
    edit: 'Edit',
    searchPlaceholder: 'Search by date or facility',
    empty: 'No transfers',
    noResultsSearch: 'Nothing found for the query “{{query}}”',
    colDate: 'Date',
    colFrom: 'From',
    colTo: 'To',
    colItemsCount: 'Products',
  },
  receipt: {
    editTitle: 'Edit receipt',
    addTitle: 'Add receipt',
    close: 'Close',
    invoiceNumberLabel: 'Invoice number:',
    invoiceNumberPlaceholder: 'INV-2026-001',
    dateLabel: 'Date:',
    datePlaceholder: 'dd/mm/yyyy',
    productPlaceholder: 'Product name',
    quantityLabel: 'Qty:',
    unitPriceLabel: 'Price per unit, ₽:',
    deleteProduct: 'Delete product',
    collapse: 'Collapse',
    expand: 'Expand',
    collapseProduct: 'Collapse product',
    expandProduct: 'Expand product',
    manufacturerLabel: 'Manufacturer:',
    supplierLabel: 'Supplier:',
    packagingLabel: 'Packaging:',
    productionDateLabel: 'Production date:',
    documentTypeLabel: 'Document type:',
    documentTypePlaceholder: 'Select a type',
    documentNumberLabel: 'Document number:',
    expiryDateLabel: 'Shelf life:',
    organolepticLabel: 'Organoleptic:',
    organolepticPlaceholder: 'Select',
    storageConditionsLabel: 'Storage conditions:',
    storageConditionsPlaceholder: 'Select',
    noteLabel: 'Note:',
    notePlaceholder: 'Optional field',
    addProduct: 'Add product',
    total: 'Total: {{value}}',
    submit: 'Create receipt',
    optional: 'optional',
    toastCreated: 'Receipt created',
    toastUpdated: 'Receipt updated',
    viewTitle: 'Receipt {{number}}',
    viewTitlePlain: 'Receipt',
    objectLabel: 'Facility:',
    amountLabel: 'Amount:',
    colProduct: 'Product',
    colQuantity: 'Qty',
    colSupplier: 'Supplier',
    colDocumentNumber: 'Document number',
    colExpiry: 'Shelf life',
    moreInfo: 'More information',
    detailManufacturer: 'Manufacturer',
    detailPackaging: 'Packaging',
    detailProductionDate: 'Production date',
    detailDocumentType: 'Document type',
    detailOrganoleptic: 'Organoleptic',
    detailStorageConditions: 'Storage conditions',
    detailUnitPrice: 'Unit price',
    detailTotal: 'Amount',
    detailNote: 'Note',
    listColItemsCount: 'Items',
    searchPlaceholder: 'Search by invoice number',
    addButton: 'Receipt',
    emptyNoObjects: 'Create a facility in the “Facilities” directory first',
    emptySearch: 'Nothing found for “{{query}}”',
    emptyNone: 'No receipts',
  },
  balances: {
    searchPlaceholder: 'Search by product or invoice',
    emptyNoObjects: 'Create a facility in the “Facilities” directory first',
    emptySearch: 'Nothing found for the query',
    emptyNone: 'No stock',
    colProduct: 'Product',
    colAvgPrice: 'Average price',
    colRemaining: 'Remaining',
    colInvoice: 'Receipt',
    colDate: 'Date',
    colPrice: 'Price',
    colReceived: 'Received',
    colExpiry: 'Best before',
    expired: 'Expired',
    expiresSoon: 'Expiring',
    expiryLoadError: 'Failed to load batch expiry dates',
  },
  meals: {
    breakfast: 'Breakfast',
    secondBreakfast: 'Second breakfast',
    lunch: 'Lunch',
    snack: 'Snack',
    dinner: 'Dinner',
  },
  metrics: {
    kcal: 'Calories',
    protein: 'Protein',
    fat: 'Fat',
    carbs: 'Carbs',
    price: 'Price',
  },
  units: {
    gram: 'g',
    ruble: '₽',
  },
  menu: {
    objectLabel: 'Facility:',
    objectPlaceholder: 'Select a facility',
    dietLabel: 'Diet:',
    dietPlaceholder: 'Select a diet',
    prevPeriod: 'Previous period',
    nextPeriod: 'Next period',
    currentWeek: 'Back to current week',
    fillMenu: 'Fill menu',
    print: 'Print (PDF)',
    today: 'Today',
    addDish: '+ dish',
    dayTotal: 'Day total',
    viewViolations: 'View violations',
    defaultName: 'Menu {{range}}',
    kcalUnit: 'kcal',
    macroProtein: 'P',
    macroFat: 'F',
    macroCarbs: 'C',
    filledToast: 'Menu filled',
    filledContent: 'Copied from menu “{{name}}”.',
    fillError: 'Could not fill the menu',
    tryAgain: 'Please try again.',
    createdToast: 'New menu created',
    createdContent: 'Menu “{{name}}” saved from the current period.',
    createError: 'Could not create the menu',
    violationAllowedToast: 'Violation allowed',
    violationAllowedContent: 'Day {{day}} saved with a norm violation.',
    priceExceededToast: 'Cell saved with price over budget',
    priceExceededContent: 'Day price {{price}} ₽ is above the limit of {{limit}} ₽.',
    slotSaved: 'Cell saved',
    createMenuTitle: 'Creating a new menu',
    fillMenuTitle: 'Fill menu',
    nameLabel: 'Menu name:',
    chooseLabel: 'Select menu:',
    choosePlaceholder: 'Select a menu',
    startLabel: 'Start on:',
    startPlaceholder: 'No upcoming days in this period',
    createFromCurrent: 'Create new from current',
    nextDayNote: 'If you select another menu, changes will apply only from the next day.',
    createNewMenu: 'Create new menu',
    choose: 'Select',
    violationsTitle: 'Violations for the day',
    violationLine: '{{label}}: {{actual}} / {{norm}} {{unit}} - {{direction}} the norm',
    below: 'below',
    above: 'above',
    violationHint: 'You can allow the violation or change the dishes in the meals.',
    allowViolation: 'Allow violation',
    printTitle: 'Menu',
    printColDay: 'Day',
    printColMeal: 'Meal',
    printColDishes: 'Dishes',
    printColKcal: 'Kcal',
    printColPrice: 'Price',
    printResponsible: 'Person responsible for nutrition: ______________________',
    printFileName: 'Menu_{{range}}.pdf',
  },
  dishPicker: {
    empty: 'No dishes yet — add some from the TTK below.',
    kcalPrice: '{{kcal}} kcal · {{price}} ₽',
    dayPriceOver: 'Day price: {{price}} / {{limit}} ₽ · over by {{over}} ₽',
    dayPriceLeft: 'Day price: {{price}} / {{limit}} ₽ · {{left}} ₽ left',
    addFromTtk: 'Add from TTK',
    searchPlaceholder: 'Search dish',
    loading: 'Loading…',
    loadError: 'Could not load the TTK',
    exceedsLimit: ' · will exceed the limit',
    add: 'Add',
    nothingFound: 'Nothing found',
    allAdded: 'All suitable dishes are already in the cell',
  },
  facility: {
    school: 'School',
    kindergarten: 'Kindergarten',
    hospital: 'Hospital',
    warehouse: 'Warehouse',
  },
  objects: {
    createTitle: 'Create object',
    editTitle: 'Edit object',
    nameLabel: 'Object name:',
    namePlaceholder: 'Central object',
    addGroupDept: 'Add group / department',
    headerFallback: 'Object',
    addObject: 'Add object',
    objectSelectLabel: 'Object:',
    objectSelectAll: 'All objects',
    subObjectLabel: 'Sub-object:',
    colGroup: 'Group / class',
    colDiet: 'Diet',
    emptySubObjectGroups: 'This sub-object has no groups or classes',
    emptyObjectGroups: 'The object has no groups or classes yet',
    groupNameObject: 'Group / department name:',
    groupNameClass: 'Class (grade):',
    groupNameGroup: 'Group:',
    groupNameDept: 'Department:',
    namePlaceholderObject: 'Building / department',
    namePlaceholderClass: 'Grades 1–4',
    namePlaceholderGroup: 'Senior group',
    namePlaceholderDept: 'Therapy',
    removeGroup: 'Remove group',
    dietHint: 'Diet (at least one must be selected)',
    noDiets: 'No diets — add them on the Diets tab.',
    addClass: 'Add class',
    addGroup: 'Add group',
    addDepartment: 'Add department',
    created: 'Object created',
    createFailed: 'Failed to create',
    updated: 'Object updated',
    saveFailed: 'Failed to save',
    deleted: 'Object deleted',
    deleteTitle: 'Delete "{{name}}"?',
    deleteDescription: 'The object is used in portions and menu. Choose how to delete it.',
    deleteAfterPeriodHint: 'Until the end of the period the object stays in portions and menu',
    deleteAfterPeriod: 'Delete after the end of the period',
    deleteNow: 'Delete now',
    deleteNowHint: 'Will be removed from all sections immediately',
    subCreateTitle: 'Add sub-object',
    subSubtitle: 'Sub-object "{{name}}"',
    subNewSubtitle: 'New sub-object',
    subNameLabel: 'Sub-object name:',
    subNamePlaceholder: 'School No. 1',
    removeSubObject: 'Remove sub-object',
    subCreated: 'Sub-object added',
    subCreateFailed: 'Failed to add',
    subUpdated: 'Sub-object updated',
    subDeleted: 'Sub-object deleted',
    subDeleteTitle: 'Delete "{{name}}"?',
    subDeleteDescription: 'The sub-object is used in portions and menu. Choose how to delete it.',
    subDeleteAfterPeriodHint:
      'Until the end of the period the sub-object stays in portions and menu',
  },
  products: {
    createTitle: 'Create product',
    editTitle: 'Edit product',
    created: 'Product created',
    updated: 'Product updated',
    deleted: 'Product deleted',
    deleteTitle: 'Delete "{{name}}"?',
    nameLabel: 'Product name:',
    namePlaceholder: 'For example, Firm tofu',
    unitLabel: 'Purchase unit:',
    unitPlaceholder: 'kg, l, pcs',
    pickPlaceholder: 'Select product',
    createOption: 'Create "{{query}}"',
    createButton: 'Create product',
    searchPlaceholder: 'Search by name',
    foundCount: 'Found: {{count}}',
    totalCount: 'Total products: {{total}}',
    emptySearch: 'Nothing found for your query',
    empty: 'No products yet',
    colName: 'Name',
    colUnit: 'Unit of measure',
    change: 'Change',
    deleteDescription:
      'The product may be used in technology cards and purchases. Choose how to delete it.',
    deleteAfterPeriod: 'Delete after the end of the period',
    deleteAfterPeriodHint:
      'Until the end of the period the product stays in technology cards and receipts',
    deleteNow: 'Delete now',
    deleteNowHint: 'Will be removed from all sections immediately',
  },
  staff: {
    personnel: 'Personnel',
    searchPlaceholder: 'Find an employee',
    emptySearch: 'No one found for "{{search}}"',
    empty: 'No employees yet',
    pageLimit:
      'Showing the first {{shown}} of {{total}}. Refine your search to find the right one.',
    colName: 'Name',
    colEmail: 'Email',
    colRegisteredAt: 'Registration',
    colRole: 'Role',
    inviteAgain: 'Invite again',
    sortAsc: 'Ascending — click for descending',
    sortDesc: 'Descending — click for ascending',
    sortBy: 'Sort by: {{label}}',
    statusUnspecified: 'unknown',
    statusInvited: 'awaiting sign-in',
    statusActive: 'active',
    statusExpired: 'invitation expired',
    addTitle: 'Add a new employee',
    addStep: 'Step {{step}}/2',
    next: 'Next',
    back: 'Back',
    addAndInvite: 'Add and send invitation',
    editTitle: 'Edit employee details',
    tabGeneral: 'General information',
    tabDocuments: 'Documents',
    change: 'Change',
    deleteTitle: 'Delete employee',
    deleteBody:
      "Delete {{name}}? Access to the system will be closed immediately and the person's account will be deleted.",
    resendTitle: 'Resend invitation',
    resendBody:
      'Send <bold>{{name}}</bold> a new invitation to {{email}}? The previous link will stop working.',
    send: 'Send',
    firstNameLabel: 'First name:',
    firstNamePlaceholder: 'Ivan',
    lastNameLabel: 'Last name:',
    lastNamePlaceholder: 'Ivanov',
    emailLabel: 'Email:',
    emailNote: 'A sign-in link will be sent to this email',
    roleLabel: 'Role:',
    rolePlaceholder: 'Select a role',
    orgEmailLabel: 'Organization e-mail:',
    snilsLabel: 'SNILS:',
    innLabel: 'TIN:',
    innPlaceholder: '12 digits',
    passportLabel: 'Passport:',
    otherDocLabel: 'Other doc:',
    otherDocPlaceholder: 'Optional',
    created: 'Employee added',
    createdContent: 'Invitation sent to {{email}}',
    updated: 'Employee details updated',
    deleted: 'Employee deleted',
    invited: 'Invitation sent',
    errSnilsInvalid: 'Invalid SNILS',
    errSnilsInvalidHint: 'The checksum did not match — check the digits',
    errInnInvalid: 'Invalid TIN',
    errInnInvalidHint: 'The check digits did not match — check the digits',
    errSnilsLength: 'Invalid SNILS',
    errSnilsLengthHint: 'SNILS must contain 11 digits',
    errInnLength: 'Invalid TIN',
    errInnLengthHint: 'TIN must contain 12 digits',
    errPassportSeries: 'Invalid passport series',
    errPassportSeriesHint: 'The series must contain 4 digits',
    errPassportNumber: 'Invalid passport number',
    errPassportNumberHint: 'The number must contain 6 digits',
    errNonDigit: 'Invalid characters',
    errNonDigitHint: 'Document numbers may contain only digits, spaces, dots and hyphens',
    errEmail: 'Invalid email',
    errEmailHint: 'Check the address',
    errFirstName: 'First name is empty',
    errLastName: 'Last name is empty',
    errAlreadyRegistered: 'Employee is already registered',
    errAlreadyRegisteredHint: 'No need to re-invite',
    errAlreadyExists: 'Such an employee already exists',
    errAlreadyExistsHint: 'This email is taken by another card',
    errEmailNotSent: 'The email was not sent',
    errEmailNotSentHint: 'The card is saved — resend the invitation from the list',
  },
  journals: {
    title: 'Automatic journals',
    desc: 'A catalog of journals that are automatically generated from menu, portions, invoices, and technology cards.',
    generating: 'Generating journal',
    generatingContent: '{{tag}}: data is pulled from related sections.',
    print: 'Print',
    auto: 'Filled automatically',
    manual: 'To be completed manually: {{fields}}',
    drawerAutoTitle: 'Filled by the system',
    drawerManualTitle: 'To be filled manually',
    drawerDesc:
      'The journal is generated from menu, portions, invoices, and technology cards. Fields for assessment and signature are filled in manually after printing or in electronic form.',
    close: 'Close',
    source: {
      menu: 'Menu',
      ttk: 'Tech cards',
      portions: 'Portions',
      invoices: 'Invoices',
      warehouse: 'Warehouse',
    },
    priority: { required: 'Required', optional: 'Optional' },
    objectLabel: 'Object',
    dietLabel: 'Diet',
    prevPeriod: 'Previous period',
    nextPeriod: 'Next period',
    currentPeriod: 'Current week',
    printResponsible: 'Responsible',
    printFileName: '{{tag}} {{range}}',
    printEmptyTitle: 'Printing a blank form',
    printEmptyContent:
      'The facility has no receipts for the selected period — blank rows are left for handwriting.',
    printNoDataContent:
      'There is no data for this journal in the selected week — blank rows are left for handwriting.',
    dayTotal: 'Day total',
    field: {
      date: 'Date',
      meal: 'Meal',
      dishes: 'Dishes',
      norm: 'Norm',
      fact: 'Actual',
      deviation: 'Deviation',
      output: 'Yield',
      time: 'Time',
      product: 'Product',
      supplier: 'Supplier',
      quantity: 'Quantity',
      shelfLife: 'Shelf life',
      condition: 'Condition assessment',
      dishType: 'Dish type',
      dish: 'Dish',
      diners: 'Number of diners',
      vitaminDose: 'Vitamin dose',
      unit: 'Unit',
      menuQuantity: 'Menu quantity',
      toIssue: 'To issue',
      period: 'Period',
      opening: 'Opening balance',
      income: 'Receipts',
      expense: 'Expense',
      closing: 'Closing balance',
      need: 'Requirement',
      stockBalance: 'Balance',
      toOrder: 'To order',
      sampleStorage: 'Sample storage',
      storekeeperSign: 'Storekeeper signature',
      sign: 'Signature',
      quality: 'Rating',
      issuePermission: 'Issue permission',
      arrivalDateTime: 'Date and time of receipt of food products',
      productName: 'Name',
      packaging: 'Packaging',
      productionDate: 'Production date',
      manufacturer: 'Manufacturer',
      arrivalQuantity: 'Quantity of the received product (in kg, litres, pcs)',
      safetyDocument:
        'Number of the document confirming the safety of the accepted food product (declaration of conformity, state registration certificate, veterinary and sanitary examination documents)',
      organoleptic:
        'Results of the organoleptic assessment of the received food raw materials and food products',
      storageConditions: 'Storage conditions, final sale deadline',
      actualSaleDateTime: 'Date and time of actual sale',
      responsibleSign: 'Signature of the responsible person',
      note: 'Note',
    },
    items: {
      'journ-01': {
        tag: 'JOURN-01',
        title: 'Cumulative dietary control sheet',
        description: 'Norm vs. actual for 10–14 days of nutrition.',
      },
      'journ-02': {
        tag: 'JOURN-02',
        title: 'Finished product grading log',
        description: 'Quality control of finished dishes before serving.',
      },
      'journ-03': {
        tag: 'JOURN-03',
        title: 'Raw material intake control log',
        description: 'Grading of perishable products on acceptance.',
      },
      'journ-04': {
        tag: 'JOURN-04',
        title: 'Fortification sheet for third and sweet dishes',
        description: 'Vitaminization of dishes by type and volume of nutrition.',
      },
      'journ-05': {
        tag: 'JOURN-05',
        title: 'Warehouse product issue sheet',
        description: 'Menu requirement for products for cooking.',
      },
      'journ-06': {
        tag: 'JOURN-06',
        title: 'Warehouse commodity report',
        description: 'Product consumption for the selected period.',
      },
      'journ-07': {
        tag: 'JOURN-07',
        title: 'Supplier request',
        description: 'Product requirements for the week.',
      },
      'journ-08': {
        tag: 'JOURN-08',
        title: 'Daily sampling log',
        description: 'Accounting of finished dish samples for storage.',
      },
    },
  },
  org: {
    rolesTitle: 'Roles and access',
    collapse: 'Collapse',
    expand: 'Expand',
    noPermissions: 'no permissions',
    listTitle: 'Organizations',
    addButton: 'Add',
    empty: 'No organizations yet',
    disableFirstTitle: 'Disable the organization first',
    disableFirstContent:
      'Deletion is irreversible, so it is available only for disabled organizations.',
    deleteConfirm: 'Delete organization "{{name}}" permanently?',
    deleted: 'Organization deleted',
    orgName: 'Name',
    memberName: 'Name',
    colMembers: 'Members',
    colStatus: 'Status',
    colRoles: 'Roles',
    colCreated: 'Created',
    colActions: 'Actions',
    colExpiresAt: 'Expires',
    disabled: 'Disabled',
    active: 'Active',
    disabledShort: 'disabled',
    enable: 'Enable',
    disable: 'Disable',
    createTitle: 'New organization',
    createName: 'Name',
    createFirstName: 'First name',
    createLastName: 'Last name',
    createAdminEmail: 'Administrator email',
    createEmailNote: 'A sign-in link will be sent to this address',
    created: 'Organization created',
    createdContent: 'Invitation sent to {{email}}',
    createFailed: 'Could not create the organization',
    removeMemberConfirm: 'Remove {{email}} from the organization?',
    membersTitle: 'Members',
    membersEmpty: 'There is no one in the organization yet — invite the first member below.',
    memberSuspended: 'suspended',
    memberActive: 'active',
    removeFromOrg: 'Remove from organization',
    invitationsTitle: 'Invitations',
    inviteEmailPlaceholder: 'email@example.com',
    inviteRolePlaceholder: 'Role',
    invite: 'Invite',
    noInvitations: 'No active invitations',
    inviteSent: 'Invitation sent',
    revoke: 'Revoke',
    statusUnspecified: 'unknown',
    statusPending: 'pending',
    statusAccepted: 'accepted',
    statusExpired: 'expired',
    statusRevoked: 'revoked',
  },
  profile: {
    personalTitle: 'Personal data',
    personalEmpty:
      'Could not load the user data. The name and email will appear if the sign-in service provides them in the profile.',
    fieldName: 'First name',
    fieldLastName: 'Last name',
    fieldEmail: 'Email',
    securityTitle: 'Security',
    securityDesc:
      'To change your password, confirm your email with the code from the message and set a new password.',
    managePassword: 'Change password',
    signOut: 'Sign out',
    preferencesTitle: 'Preferences',
    themeLabel: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    languageLabel: 'Interface language',
    languageCaption: 'The interface switches instantly; the choice is saved in this browser.',
  },
  invitation: {
    missingTitle: 'Incomplete link',
    missingMessage:
      'The invitation is missing the address or the one-time token. Ask the administrator to send the invitation again.',
    toHome: 'Go to home',
    alreadyTitle: 'You are already signed in with another account',
    alreadyMessage:
      'The invitation was issued to {{email}}. To accept it, sign out of the current account and open the link from the email again.',
    signOut: 'Sign out',
    stay: 'Stay in the current account',
    failedTitle: 'Could not start sign-in',
    retry: 'Try again',
    accepting: 'Accepting the invitation…',
    redirecting: 'The sign-in page for {{email}} will open now.',
    continueManually: 'Continue manually',
  },
  diets: {
    nameLabel: 'Name:',
    namePlaceholder: 'For example, Standard',
    institutionsLabel: 'Which institutions the diet is available for:',
    mealsLabel: 'Which meals the diet is available for:',
    dailyNorm: 'Daily norm',
    pricePerDayLabel: 'Price per day, ₽:',
    formKcal: 'Kcal:',
    formProtein: 'Protein, g:',
    formFat: 'Fat, g:',
    formCarbs: 'Carbs, g:',
    normError: 'Cannot be negative',
    availableFor: 'Available for:',
    mealsTitle: 'Meals:',
    viewPrice: 'Price:',
    viewKcal: 'Calories:',
    viewProtein: 'Protein:',
    viewFat: 'Fat:',
    viewCarbs: 'Carbs:',
    editDietTitle: 'Edit diet',
    createDietTitle: 'Create diet',
    createDiet: 'Create diet',
    deleteConfirmTitle: 'Delete “{{name}}”?',
    deleteConfirmText: 'The diet is used in facilities and menus. Choose how to delete it.',
    deleteAfterPeriod: 'Delete after the period ends',
    deleteAfterPeriodHint: 'The diet will remain in facilities and menus until the period ends',
    deleteNow: 'Delete now',
    deleteNowHint: 'Will be removed from all sections immediately',
    updatedToast: 'Diet saved',
    deletedToast: 'Diet deleted',
    createdToast: 'Diet created',
    searchPlaceholder: 'Search by diet name',
    total: 'Total diets: {{count}}',
    emptySearch: 'Nothing found for “{{query}}”',
    emptyNone: 'No diets yet',
    summary: '{{price}} ₽ · {{kcal}} kcal · P: {{protein}} g · F: {{fat}} g · C: {{carbs}} g',
  },
  spread: {
    objectLabel: 'Facility:',
    objectPlaceholder: 'Select a facility',
    wholeOrg: 'Whole organization',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    byMeals: 'By meals',
    byProducts: 'By products',
    printPdf: 'Download layout as PDF',
    searchPlaceholder: 'Search products...',
    foundProducts: 'Found: {{count}} products',
    totalProducts: 'Total products: {{count}}',
    confirmedToast: 'Products written off from the warehouse',
    alreadyConfirmed: 'Products already written off from the warehouse',
    confirmHint: 'Write-off can be confirmed for past days, today and tomorrow',
    confirmProducts: 'Confirm product write-off',
    emptySpread: 'No layout: fill in the portioning for this date',
    confirmTitle: 'Confirm write-off',
    confirmText: 'Check the information — this cannot be changed.',
    ok: 'OK',
    colProduct: 'Product',
    colTotalDay: 'Total for the day',
    colDish: 'Dish',
    colMeal: 'Meal',
    colPortions: 'Portions',
    colQuantity: 'Quantity',
    printTitle: 'Product layout',
    printPositions: 'Total positions: {{count}}',
    printTotalDay: 'Total products for the day: {{count}}',
    printFileName: 'Layout_{{date}}.pdf',
  },
  accounts: {
    objectLabel: 'Facility:',
    allObjects: 'All facilities',
    totalAccounts: 'Total invoices: {{count}}',
    found: 'Found: {{count}}',
    passedPeople: 'Attended: {{count}} people',
    chargedRub: 'Charged: {{amount}} ₽',
    people: '{{count}} people',
    amountRub: '{{amount}} ₽',
    print: 'Print',
    colOrganization: 'Organization',
    colPassed: 'Attended',
    colCharged: 'Charged',
  },
  portions: {
    prevDay: 'Previous day',
    nextDay: 'Next day',
    byObjects: 'By facilities',
    byDishes: 'By dishes',
    titleObjects: 'Portioning by facilities',
    titleDishes: 'Portioning by dishes',
    searchObjects: 'Search facilities...',
    searchDishes: 'Search dishes...',
    objectLabel: 'Facility:',
    objectPlaceholder: 'Select a facility or sub-facility',
    print: 'Print (PDF)',
    futureDate: 'This date is in the future',
    goToday: 'Go to today',
    fillYesterday: "Fill with yesterday's data",
    emptyAddSubObjects: 'First add sub-facilities in the “Facilities” section',
    fillError: 'Could not fill',
    saveError: 'Could not save',
    stickersTitle: 'Thermos stickers',
    stickerTitle: 'Sticker: {{dish}}',
    expiryLabel: 'Shelf life:',
    expiryPlaceholder: 'For example “72 hours” or “until 12.10.2026”',
    downloadPdf: 'Download PDF',
    total: 'Total',
    emptySummary: 'No data for the summary portioning',
    colObject: 'Facility',
    colDepartment: 'Department',
    colTable: 'Table',
    colPeople: 'People',
    colDish: 'Dish',
    colDiet: 'Diet',
    colPortions: 'Portions',
    subObjectOf: 'sub-facility {{name}}',
    noData: 'No data',
    fillByOrg: 'Fill in the portioning by facilities',
    fillPortions: 'Fill portioning',
    grandTotalLabel: 'Total amount of all dishes',
    stickerTooltip: 'Thermos sticker',
    kg: 'kg',
    printTitle: 'Portioning',
    printResponsible: 'Person responsible for nutrition: ______________________',
    printFileName: 'Portioning_{{date}}.pdf',
    stickerWeight: 'Weight per portion: {{grams}} g',
    stickerPortionsKg: '{{portions}} portions ({{kg}} kg)',
    stickerComposition: 'Ingredients: {{composition}}',
    stickerKbju: 'KCAL: P {{protein}} · F {{fat}} · C {{carbs}} · {{kcal}} kcal',
    stickerDate: 'Date: {{date}}',
    stickerExpiry: 'Shelf life: {{expiry}}',
    stickerFileName: 'Stickers_{{date}}.pdf',
  },
  buffet: {
    prevDay: 'Previous day',
    nextDay: 'Next day',
    assortment: 'Assortment',
    transfer: 'Transfer',
    sale: 'Sales',
    writeOff: 'Write off',
    revenue: 'Revenue',
    cost: 'Cost',
    margin: 'Margin',
    writeOffs: 'Write-offs',
    colProduct: 'Product',
    colPrice: 'Price',
    colOpening: 'Opening',
    colReceived: 'Received',
    colSold: 'Sold',
    colWrittenOff: 'Written off',
    colClosing: 'Closing',
    colRevenue: 'Revenue',
    emptyNoObjects: 'First add a facility in the “Facilities” section',
    emptyAssortment: 'There are no products in the assortment yet',
    emptyNoStock: 'No products in stock — transfer them from the warehouse first',
    emptyDay: 'No movements on {{date}}',
    setupAssortment: 'Set up assortment',
    assortmentTitle: 'Assortment',
    assortmentHint:
      'Pick products from the shared catalogue and set a sale price. Purchase prices stay in the warehouse.',
    searchProducts: 'Search products...',
    noProducts: 'There are no products in the catalogue. Add them in the “Products” section.',
    emptyProductSearch: 'Nothing found for “{{query}}”',
    salePricePlaceholder: 'Price, ₽',
    priceRequired: 'Set a price greater than zero for the selected products',
    assortmentSaved: 'Assortment saved',
    transferTitle: 'Transfer from warehouse',
    saleTitle: 'Record sales',
    writeOffTitle: 'Write off',
    reason: 'Write-off reason',
    reasonPlaceholder: 'For example “expired”',
    productPlaceholder: 'Select a product',
    productRemaining: '{{name}} · {{remaining}} left',
    errorSelectProduct: 'Select a product',
    errorDuplicateProduct: 'This product is already in another row',
    errorQuantity: 'Enter a quantity greater than zero',
    errorNotMore: 'No more than {{max}}',
    addRow: 'Add row',
    applyMovement: 'Post',
    transferSaved: 'Transfer posted',
    saleSaved: 'Sales recorded',
    writeOffSaved: 'Write-off posted',
    dataUnavailable: 'Could not load data. Reload the page.',
  },
  ttk: {
    title: 'Tech card',
    createTitle: 'Create tech card',
    editTitle: 'Edit tech card',
    editDishTitle: 'Edit dish',
    created: 'Tech card created',
    saved: 'Tech card saved',
    deleted: 'Tech card deleted',
    deletedInMenu: 'It will stay in the menu until the end of the period',
    saveCurrent: 'Save to current',
    saveNew: 'Save as new',
    yieldLabel: 'Yield:',
    collectionLabel: 'Collection:',
    technologyLabel: 'Technology:',
    noIngredients: 'No ingredients specified',
    colTitle: 'Name',
    colCollection: 'Collection',
    colPrice: 'Price',
    colKcal: 'Kcal',
    colName: 'Name',
    colGross1: '1 portion, gross',
    colNet1: '1 portion, net',
    colGross100: '100 portions, gross',
    colNet100: '100 portions, net',
    colUnitPrice: 'Unit price',
    colSum: 'Amount',
    noStock: 'no stock',
    proteinG: 'Protein, g',
    fatG: 'Fat, g',
    carbsG: 'Carbs, g',
    kcalCol: 'Energy value, kcal',
    nameLabel: 'Name:',
    namePlaceholder: 'Dish name',
    collectionNameLabel: 'Collection name:',
    collectionNamePlaceholder: 'Collection name',
    optional: 'optional',
    technologySection: 'Cooking technology',
    technologyPlaceholder: 'Describe the cooking technology',
    nutritionSection: 'Nutrition per portion',
    proteinLabel: 'Protein, g:',
    fatLabel: 'Fat, g:',
    carbsLabel: 'Carbs, g:',
    kcalLabel: 'Calories, kcal:',
    ingredientsSection: 'Ingredients',
    productNamePlaceholder: 'Product name',
    grossLabel: 'Gross per 1 portion:',
    netLabel: 'Net per 1 portion:',
    removeIngredient: 'Remove ingredient',
    addIngredient: 'Add ingredient',
    errorSelectProduct: 'Select a product from the catalog',
    errorGrossPositive: 'Gross must be greater than zero',
    errorGrossDecimals: 'Gross allows at most three decimal places',
    errorNetRange: 'Net must be between zero and gross',
    errorNetDecimals: 'Net allows at most three decimal places',
    errorDuplicateProduct: 'This product is already in the card',
    requiredField: 'Required field',
    allCollections: 'All',
    noCollection: 'No collection',
    searchPlaceholder: 'Search by dish or card name',
    foundCount: 'Found: {{count}} tech cards',
    totalCount: 'Total tech cards: {{count}}',
    nothingFound: 'Nothing found for the given filters',
    empty: 'No tech cards yet',
    sortAsc: 'Ascending — click to sort descending',
    sortDesc: 'Descending — click to sort ascending',
    sortBy: 'Sort by: {{label}}',
    importHeader: 'Import collections',
    importIntro: 'Import your collections',
    importButton: 'Import',
    importCollection: 'Import collection',
    importUnsupported: 'Unsupported format',
    importOnlyFormats: 'Only {{formats}} can be uploaded',
    importSomeFailed: 'Some files were not parsed',
    importEmptyFiles: 'No dishes found in: {{files}}',
    importNone: 'No dishes found',
    importNoneHint: 'Check that the file contains tech cards or dish names.',
    importParseError: 'File parsing error',
    importNotConnected: 'Collection import is not connected yet',
    importNotConnectedHint:
      'Cards from the file were not saved. Create them manually via “Create tech card”.',
    renameFile: 'Rename file',
    removeFile: 'Remove file',
    collapse: 'Collapse',
    showDishes: 'Show dishes',
    renameCollection: 'Rename',
    removeCollection: 'Remove collection',
    openEdit: 'Open and edit',
    removeDish: 'Remove dish',
    chooseFile: 'Choose a file',
    or: 'or',
    dropHere: 'drag the file here',
    deleteHeader: 'Delete “{{name}}”?',
    deleteDescription:
      'The tech card is used in the menu and other sections. Choose how to delete it.',
    deleteSoft: 'Delete after the end of the period',
    deleteSoftHint:
      'Until the end of the period the tech card stays in the menu and other sections',
    deleteNow: 'Delete now',
    deleteNowHint: 'It will be removed from all sections immediately',
  },
  apiErrors: {
    fallback: {
      network: 'No connection to the server. Check your internet connection and try again',
      unknown: 'Something went wrong. Please try again',
      invalidArgument: 'Please check the entered data',
      notFound: 'Not found — it may have been deleted. Refresh the page',
      alreadyExists: 'This already exists',
      permissionDenied: "You don't have permission to do this",
      conflict: "This can't be done right now: the data has changed or is used elsewhere",
      rateLimited: 'Too many requests. Wait a moment and try again',
      unavailable: 'The service is temporarily unavailable. Please try again later',
      unauthenticated: 'Your session has expired. Please sign in again',
    },
    codes: {
      BAD_REQUEST: "The request couldn't be completed. Refresh the page and try again",
      BATCH_DUPLICATE: 'Line {{line}}: this batch is already in the document',
      BATCH_NOT_FOUND:
        'Line {{line}}: the batch was not found — the goods receipt may have been changed',
      BATCH_OTHER_OBJECT: 'Line {{line}}: this batch belongs to another facility',
      BATCH_RECEIVED_AFTER_DATE:
        "Line {{line}}: the batch was received on {{receiptDate}}, the document date can't be earlier",
      BATCH_REMAINING_EXCEEDED:
        'Line {{line}}: only {{remainingQuantity}} left in the batch, but {{quantity}} was entered',
      BATCH_REQUIRED: 'Line {{line}}: select a batch',
      DATE_INVALID: 'Invalid date',
      DATE_RANGE_INVALID: 'The end date is before the start date',
      DATE_REQUIRED: 'Enter a date',
      DIET_NAME_EMPTY: 'Enter the diet name',
      DIET_NORM_NEGATIVE: "Diet norms can't be negative",
      DIET_NOT_FOUND: 'Diet not found — it may have been deleted',
      EMPLOYEE_ALREADY_EXISTS: 'This employee already exists',
      EMPLOYEE_ALREADY_REGISTERED: 'This employee has already signed up — no invitation needed',
      EMPLOYEE_EMAIL_INVALID: 'Check the employee’s email address',
      EMPLOYEE_EMAIL_TAKEN: 'This email is already in use — enter a different one',
      EMPLOYEE_FIELD_DIGITS_COUNT: 'The “{{field}}” field must contain {{digits}} digits',
      EMPLOYEE_FIELD_DIGITS_ONLY: 'The “{{field}}” field may contain digits only',
      EMPLOYEE_FIELD_REQUIRED: 'Please fill in the “{{field}}” field',
      EMPLOYEE_INN_INVALID: 'The INN number is incorrect — please check the digits',
      EMPLOYEE_NOT_FOUND: 'Employee not found — they may have been removed',
      EMPLOYEE_SNILS_INVALID: 'The SNILS number is incorrect — please check the digits',
      INVITATION_EMAIL_INVALID: 'Check the invitation email address',
      INVITATION_EMAIL_NOT_SENT:
        'Saved, but the invitation email was not sent — send the invitation again',
      INVITATION_EMAIL_REQUIRED: 'Enter the email to send the invitation to',
      INVITATION_NOT_FOUND: 'Invitation not found — it may have been revoked',
      ITEMS_REQUIRED: 'Add at least one line',
      MEMBER_NOT_FOUND: 'Member not found — they may have been removed',
      MENU_DUPLICATE_RECIPE: 'This dish is already in the meal',
      MENU_NAME_EMPTY: 'Enter the menu name',
      MENU_NOT_FOUND: 'Menu not found — it may have been deleted',
      MENU_RANGE_TOO_LONG: "The period can't be longer than {{maxDays}} days",
      MENU_RECIPE_NOT_FOUND: 'One of the tech cards was not found — it may have been deleted',
      NOT_ENOUGH_STOCK: 'Not enough stock in the warehouse: {{items}}',
      NUMBER_INVALID: 'Enter a number',
      OBJECT_DIET_NOT_FOUND:
        'One of the diets in the groups was not found — it may have been deleted',
      OBJECT_GROUP_NAME_EMPTY: 'Enter the group name in row {{line}}',
      OBJECT_HAS_RECEIPTS: "The facility has goods receipts — it can't be deleted",
      OBJECT_NAME_EMPTY: 'Enter the facility name',
      OBJECT_NOT_FOUND: 'Facility not found — it may have been deleted',
      OBJECT_TYPE_UNKNOWN: 'Select a facility type from the list',
      ORGANIZATION_ADMIN_EMAIL_TAKEN:
        'This email is already in use — enter a different one for the administrator',
      ORGANIZATION_IS_PLATFORM: "The platform's service organization can't be deleted",
      ORGANIZATION_NAME_EMPTY: 'Enter the organization name',
      ORGANIZATION_NOT_DISABLED: 'Disable the organization first, then it can be deleted',
      ORGANIZATION_NOT_FOUND: 'Organization not found — it may have been deleted',
      ORGANIZATION_REQUIRED: 'Select an organization',
      PORTIONS_COPY_SAME_DAY: "A day can't be copied onto itself — choose another date",
      PORTIONS_GROUP_DIET_NOT_FOUND:
        'This group and diet were not found in the facility — it may have been changed',
      PORTIONS_HEADCOUNT_NEGATIVE: "Headcount can't be negative",
      PRODUCT_HAS_OPEN_BATCHES:
        'The product is in stock — it can be deleted once the stock runs out',
      PRODUCT_HAS_RECEIPTS:
        "The product has already been received into the warehouse — it can't be changed",
      PRODUCT_NAME_EMPTY: 'Enter the product name',
      PRODUCT_NOT_FOUND: 'Product not found — it may have been deleted',
      PRODUCT_REQUIRED: 'Line {{line}}: select a product',
      PRODUCT_UNIT_UNKNOWN: 'Select a unit from the list',
      PRODUCT_USED_IN_RECIPES: "The product is used in tech cards — it can't be changed or deleted",
      QUANTITY_NOT_POSITIVE: 'Line {{line}}: the quantity must be greater than zero',
      RECEIPT_BELONGS_TO_TRANSFER:
        'This goods receipt was created by a transfer — edit the transfer instead',
      RECEIPT_DOCUMENT_NUMBER_REQUIRED: 'Line {{line}}: please enter the document number',
      RECEIPT_DOCUMENT_TYPE_REQUIRED: 'Line {{line}}: please enter the document type',
      RECEIPT_EXPIRY_DATE_REQUIRED: 'Line {{line}}: please enter the expiry date',
      RECEIPT_HAS_WRITE_OFFS:
        "Products from this goods receipt have already been written off or transferred, so it can't be changed",
      RECEIPT_INVOICE_NUMBER_REQUIRED: 'Please enter the goods receipt number',
      RECEIPT_ITEM_NOT_FOUND: 'A goods receipt line was not found — refresh the page and try again',
      RECEIPT_MANUFACTURER_REQUIRED: 'Line {{line}}: please enter the manufacturer',
      RECEIPT_NOT_FOUND: 'The goods receipt was not found',
      RECEIPT_ORGANOLEPTIC_REQUIRED:
        'Line {{line}}: please enter the organoleptic assessment result',
      RECEIPT_PACKAGING_REQUIRED: 'Line {{line}}: please enter the packaging',
      RECEIPT_PRODUCTION_DATE_REQUIRED: 'Line {{line}}: please enter the production date',
      RECEIPT_STORAGE_CONDITIONS_REQUIRED: 'Line {{line}}: please enter the storage conditions',
      RECEIPT_SUPPLIER_REQUIRED: 'Line {{line}}: please enter the supplier',
      RECIPE_DUPLICATE_PRODUCT: 'The product in row {{line}} is already in the ingredients',
      RECIPE_GROSS_NOT_POSITIVE: 'Gross weight in row {{line}} must be greater than zero',
      RECIPE_INGREDIENT_PRODUCT_EMPTY: 'Select a product in row {{line}}',
      RECIPE_NAME_EMPTY: 'Enter the tech card name',
      RECIPE_NET_OUT_OF_RANGE: 'Net weight in row {{line}} must be between zero and gross',
      RECIPE_NOT_FOUND: 'Tech card not found — it may have been deleted',
      RECIPE_NUTRITION_NEGATIVE: "Nutritional values can't be negative",
      RECIPE_PRODUCT_NOT_FOUND:
        'One of the products in the tech card was not found — it may have been deleted',
      RECIPE_USED_IN_MENU: "The tech card is used in a menu — it can't be changed or deleted",
      RECIPE_YIELD_NEGATIVE: "Portion yield can't be negative",
      ROLE_NOT_ASSIGNABLE: 'This role can’t be assigned to an employee — choose another one',
      SPREAD_ALREADY_CONFIRMED: 'The product layout for {{date}} has already been confirmed',
      SPREAD_DATE_TOO_LATE: 'The product layout can be confirmed no further ahead than tomorrow',
      SPREAD_EMPTY: 'There is no product layout for {{date}}',
      TOO_MANY_DECIMALS: 'No more than {{max}} decimal places',
      TRANSFER_FROM_OBJECT_REQUIRED: 'Select the facility to transfer from',
      TRANSFER_HAS_WRITE_OFFS:
        "The transferred products have already been written off or moved on, so the transfer can't be changed",
      TRANSFER_NOT_FOUND: 'The transfer was not found',
      TRANSFER_SAME_OBJECTS: "You can't transfer products to the same facility",
      TRANSFER_TO_OBJECT_REQUIRED: 'Select the facility to transfer to',
      UNIT_PRICE_NEGATIVE: 'Line {{line}}: the price cannot be negative',
      WRITE_OFF_AUTO_NOT_EDITABLE:
        "An automatic write-off from the product layout can't be changed",
      WRITE_OFF_NOT_FOUND: 'The write-off was not found',
      WRITE_OFF_REASON_REQUIRED: 'Please enter the reason for the write-off',
    },
    fields: {
      email: 'Email',
      firstName: 'First name',
      inn: 'INN',
      lastName: 'Last name',
      passportNumber: 'Passport number',
      passportSeries: 'Passport series',
      roleId: 'Role',
      snils: 'SNILS',
    },
  },
  statistics: {
    objectLabel: 'Facility:',
    allObjects: 'All facilities',
    preset7: '7 days',
    preset30: '30 days',
    presetMonth: 'This month',
    presetPrevMonth: 'Previous month',
    demoEnable: 'Demo data',
    demoDisable: 'Real data',
    demoBadge: 'Demo data',
    demoHint:
      'Generated invoices for the last four months, so you can preview the page without real data.',
    kpiPeople: 'Person-days',
    kpiPeopleHint: 'On average {{value}} people per day',
    kpiCharged: 'Charged for the period',
    kpiChargedHint: 'On average {{value}} per day',
    kpiPerPerson: 'Cost per person-day',
    kpiPerPersonHint: 'What one person costs per day',
    kpiDays: 'Days with data',
    kpiDaysHint: '{{percent}} of the period days have data',
    chartPeople: 'People by day',
    chartCharged: 'Spend by day',
    chartEmpty: 'No data for the selected period',
    maxValue: 'Maximum: {{value}}',
    byDays: 'By day',
    byMonths: 'By month',
    avgPerMonth: 'Monthly average: {{value}}',
    byObjects: 'By facility',
    byDiets: 'By diet',
    byMeals: 'By meal',
    colDate: 'Date',
    colMonth: 'Month',
    colObject: 'Facility',
    colPeople: 'People',
    colPeopleDays: 'Person-days',
    colCharged: 'Charged',
    colPerPerson: '₽/person',
    colPerDay: '₽/day',
    colShare: 'Share',
    portionsValue: '{{value}} portions',
    daysWithoutData: 'Days without invoices: {{count}}',
    total: 'Total',
    empty:
      'There are no invoices for the selected period. They appear after the product layout is confirmed.',
    budgetTitle: 'Spend vs limit',
    budgetHint:
      'What was actually spent on meals and what the limit allowed: the limit is the diet day price times the people on that diet that day.',
    budgetSpent: 'Spent',
    budgetPeopleDays: '{{value}} person-days',
    budgetLimit: 'Limit',
    budgetLimitHint: 'By diet day prices',
    budgetPercent: 'Limit used',
    budgetSaved: 'Saved {{value}}',
    budgetOver: 'Overspent {{value}}',
    budgetWithoutLimit:
      '{{value}} more was charged to diets without a day price (deleted or unset) and is left out of the comparison.',
    budgetByDiets: 'Limit by diet',
    budgetByObjects: 'Limit by facility',
    colDiet: 'Diet',
    colSpent: 'Spent',
    colLimit: 'Limit',
    colLimitPercent: '%',
  },
};

export const resources = {
  ru: { translation: ru },
  en: { translation: en },
};
